import type { Instance, SnapshotIn, SnapshotOut } from "mobx-state-tree";
import { types, flow, getRoot } from "mobx-state-tree";
import { observable } from "mobx";
import * as Sentry from "@sentry/react";
import { CORNER_WIDTH } from "@/components/layout/bottomRightStack";
import { bindTrackToCurrentUser } from "@/lib/posthog";
import {
  BOTTOM_MARGIN,
  COLLAPSED_HEIGHT,
  COLLAPSED_WIDTH,
  TOP_OFFSET,
} from "@/components/chat/SidebarChat/chatWidgetLayout";
import { CHAT_ATTACHMENT_PENDING_STATES } from "@/domains/documents/utils/documentState";
import * as chatRepo from "./repository";
import { getExistingDocumentFilenames } from "../documents/repository";
import { ChatSendRejectedError, assertChatMessageSendable } from "./messageLimits";
import type { ChatRootAccess } from "../rootAccess";
import { withAbort } from "./abort";
import { logger } from "@/utils/logger";

/** A document chip as a message carries it: moved out of the composer on Send. */
export interface PendingAttachmentDocument {
  id: string;
  filename: string;
  state: string | null;
}

/** An issue chip as a message carries it. */
export interface PendingAttachmentIssue {
  id: number;
  issue_number: number;
  title: string;
}

/** Everything attached to one message, taken from the composer when it was sent. */
export interface PendingAttachments {
  documents: PendingAttachmentDocument[];
  issues: PendingAttachmentIssue[];
}

/**
 * A message parked until it can go: behind a turn that is still running, or
 * waiting for its own attachments to finish uploading. It carries an id
 * because the queue is mutated from two directions at once — the flush effect
 * drains the head while the user cancels a bubble — and a position taken at
 * render time is stale by the time the click lands.
 *
 * Its attachments travel with it. They leave the composer the moment the user
 * presses Send, so a chip can never be left behind in the composer, and a chip
 * attached while the message waits belongs to the NEXT message, not this one.
 */
export interface PendingSend {
  id: string;
  text: string;
  attachments: PendingAttachments;
  fromSuggestion?: boolean;
}

let pendingSendSeq = 0;
let uploadPlaceholderSeq = 0;

interface AssistantSendAnalytics {
  suggestionUsed: boolean;
  attachmentCount: number;
}

/**
 * State of a document chip whose file is still uploading. Its id is a client
 * placeholder (`upload-N`) until the document row exists, so nothing can be
 * sent with it yet.
 */
export const UPLOADING_STATE = "UPLOADING";
/** State of a chip whose upload failed after its message was already sent. */
export const UPLOAD_FAILED_STATE = "UPLOAD_FAILED";

/** Whether a parked message can go, must keep waiting, or lost an attachment. */
export type PendingSendReadiness = "ready" | "uploading" | "uploadFailed";

const MAX_FILENAME_CANDIDATES = 40;

/**
 * WHA-3330: pipeline states where the doc is not yet readable by the
 * chatbot's `read_document` tool. The chip shows a spinner while a doc
 * is in any of these, and the optimistic-send queue holds the SSE
 * dispatch until every attached doc has left this set. Built from the
 * shared CHAT_ATTACHMENT_PENDING_STATES, like the chip spinner predicate in
 * SidebarChatInput.tsx and the issue panel's "Processing" label.
 */
const PRE_EXTRACTION_STATES = new Set<string>([UPLOADING_STATE, ...CHAT_ATTACHMENT_PENDING_STATES]);

/**
 * The longest a sent message waits for its attachments before it is sent
 * anyway. The hold exists so the chatbot is not asked about a file it cannot
 * read yet; a stalled or parked parse must cost the user a degraded answer
 * ("still processing"), never a question that is silently never sent while
 * the thread stays locked behind it.
 */
export const QUEUED_SEND_MAX_WAIT_MS = 3 * 60_000;
/**
 * How long a document a send went out without is still watched after that
 * send. Long enough for a slow IFC parse to finish and its chip to settle;
 * a parse that never finishes stops costing a channel and a poll after it.
 */
export const STALLED_ATTACHMENT_TRACKING_MS = 15 * 60_000;
const FILENAME_CANDIDATE_REGEX =
  /([^\n`"'<>|:]{1,220}?\.(?:pdf|docx?|xlsx?|csv|dwg|txt|png|jpe?g|gif|webp|svg|bmp|tiff?|zip|asice))/gi;
const LEADING_FILENAME_STOP_WORDS = new Set([
  "a",
  "an",
  "and",
  "at",
  "can",
  "could",
  "document",
  "file",
  "filename",
  "for",
  "from",
  "i",
  "in",
  "my",
  "of",
  "on",
  "open",
  "or",
  "our",
  "page",
  "please",
  "review",
  "see",
  "show",
  "that",
  "the",
  "this",
  "to",
  "we",
  "with",
  "your",
]);

function dedupeFilenames(filenames: string[]): string[] {
  const normalized = filenames
    .map((name) => name.trim())
    .filter((name) => Boolean(name))
    .map((name) => name.replace(/\\/g, "/").split("/").pop() ?? name);
  return Array.from(new Set(normalized));
}

function cleanFilenameCandidate(candidate: string): string {
  let cleaned = candidate
    .trim()
    .replace(/^["'`()\[\]{}<>]+|["'`()\[\]{}<>]+$/g, "")
    .replace(/[.,;:!?]+$/g, "")
    .trim();

  // Strip leading punctuation separators from semicolon/comma-delimited lists.
  cleaned = cleaned.replace(/^[;,:!?]+\s*/, "");

  // Strip markdown list prefixes (e.g. "- ", "* ", "+ ")
  cleaned = cleaned.replace(/^[-*+]\s+/, "");

  const words = cleaned.split(/\s+/).filter(Boolean);
  while (words.length > 1 && LEADING_FILENAME_STOP_WORDS.has(words[0].toLowerCase())) {
    words.shift();
  }
  cleaned = words.join(" ");

  return cleaned;
}

function extractFilenameCandidates(message: string): string[] {
  const matches = (message.match(FILENAME_CANDIDATE_REGEX) ?? []).map(cleanFilenameCandidate);
  return dedupeFilenames(matches).slice(0, MAX_FILENAME_CANDIDATES);
}

function findLastAssistantIndex(messages: Array<{ role: "user" | "assistant" }>): number {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    if (messages[i].role === "assistant") {
      return i;
    }
  }
  return -1;
}

function normalizeRollbackMessages(
  messages: chatRepo.SessionMessage[],
  rolledBackToMessageId: string | null
): chatRepo.SessionMessage[] {
  if (messages.length === 0) {
    return [];
  }

  if (rolledBackToMessageId) {
    const rollbackIndex = messages.findIndex((message) => message.id === rolledBackToMessageId);
    if (rollbackIndex >= 0) {
      return messages.slice(0, rollbackIndex + 1);
    }
  }

  const lastAssistantIndex = findLastAssistantIndex(messages);
  if (lastAssistantIndex < 0) {
    return [];
  }

  return messages.slice(0, lastAssistantIndex + 1);
}

/**
 * A document the chatbot generated during a turn (WHA-3118).
 * Held in-memory only — base64 payload is not persisted across reloads.
 */
export const GeneratedFile = types.model("GeneratedFile", {
  id: types.string,
  filename: types.string,
  mime: types.string,
  sizeBytes: types.number,
  dataB64: types.string,
});

const CHAT_ROLES = ["user", "assistant"] as const;

export const ChatMessage = types.model("ChatMessage", {
  id: types.maybeNull(types.string),
  role: types.enumeration("Role", CHAT_ROLES),
  content: types.string,
  sourceFilenames: types.optional(types.array(types.string), []),
  generatedFiles: types.optional(types.array(GeneratedFile), []),
  choices: types.optional(types.array(types.string), []),
  choiceQuestion: types.optional(types.string, ""),
  isError: types.optional(types.boolean, false),
  usedDocumentTool: types.optional(types.boolean, false),
  ehrLink: types.maybeNull(types.string),
});

export const AttachedDocument = types.model("AttachedDocument", {
  id: types.string,
  filename: types.string,
  // WHA-3356: document state from public.documents.state. Used to show a
  // spinner on the chip while extraction is still in flight. May be null
  // for legacy attached docs (already-uploaded files where state is
  // assumed to be ready) — the UI treats null as "ready".
  state: types.maybeNull(types.string),
});

// WHA-3336: issue context attached from the /issues bulk-action
// toolbar. Stored alongside attachedDocuments and folded into the user
// message as a `[ATTACHED ISSUES]` block on send, mirroring the doc
// attachment flow.
export const AttachedIssue = types.model("AttachedIssue", {
  id: types.number,
  issue_number: types.number,
  title: types.string,
});

export const ChatThread = types.model("ChatThread", {
  threadId: types.identifier,
  buildingId: types.string,
  orgId: types.string,
  sessionId: types.maybeNull(types.string),
  messages: types.array(ChatMessage),
  isHydrated: types.optional(types.boolean, false),
});

/**
 * Chat session model for multi-chat support (WHA-1465)
 */
export const ChatSession = types.model("ChatSession", {
  id: types.identifier,
  title: types.string,
  threadId: types.string,
  buildingId: types.string,
  createdAt: types.string,
  lastMessageAt: types.maybeNull(types.string),
  messageCount: types.number,
});

export type ViewMode = "collapsed" | "sidebar" | "expanded";
type SessionPrewarmState = "prewarming" | "ready" | "context_only" | "failed";

/** One confirmation card the backend is blocked on, waiting for this user. */
export type PendingToolConfirmation = {
  questionId: string;
  payload: chatRepo.ToolConfirmationPayload;
  /** The chatbot pod holding the waiting coroutine, echoed back on the answer
   *  POST so it reaches that pod rather than a round-robined sibling. */
  server: string | null;
};

/** How the head-of-queue card's submission is going. Distinct from the card
 *  itself, because a failed submit must KEEP the card: clearing it optimistically
 *  and swallowing the error left the user with a spinner, no buttons, and a
 *  question id nobody could answer. */
export type ToolConfirmationSubmission = {
  /** The card this state belongs to. Scoped per question, not per thread: an
   *  expired head is retired and the NEXT card takes its place, which would
   *  otherwise inherit "Franklin has stopped waiting" and a retry button
   *  belonging to a question it has nothing to do with. */
  questionId: string;
  /** Which button the user pressed. A failure puts "Try again" on THAT button:
   *  labelling Approve as the retry after a failed Deny hangs the retry
   *  affordance off the one button that performs the write. */
  action: "approve" | "deny";
  state: "submitting" | "failed";
  /** i18n key for the failure, so the card can offer a retry that means something. */
  errorKey?: "submitFailed" | "expired";
  /**
   * This failure was a 5xx. Only a 5xx says anything about the pod `?route=`
   * names, so unpinning counts CONSECUTIVE ones: recorded per failure, a
   * transport error or a retryable 404 overwrites it falsy and the count
   * starts again. Without that an offline TypeError followed by a single 502
   * unpinned — a one-way move away from the only replica holding the waiter.
   */
  serverError?: boolean;
};

/**
 * A teardown deny that never lands leaves a tool coroutine holding the
 * session's conversation lock until the HITL timeout — a stall with no user
 * left on screen to report it, so it has to be visible in production.
 * `ConfirmationExpiredError` is the ordinary outcome here (the backend had
 * already stopped waiting) and is not a failure — but it is raised only for a
 * ROUTED 404, i.e. the pod that raised the card confirming the question is
 * gone.
 *
 * A routeless 404 says the SAME thing far more often than it says "the
 * coroutine is stuck on another replica": every card the backend raised
 * without a `server` (single replica, or a backend older than chatbot#181)
 * denies routelessly, and an ordinary stream end walks the whole queue — so
 * capturing those made one Sentry error per stale card, expected-path noise
 * that buries the deny which genuinely did not land. 404 is logged and
 * dropped; a 5xx or a transport failure, where the question is still waiting
 * somewhere, is captured.
 */
function reportConfirmationDenyFailure(questionId: string, err: unknown): void {
  if (err instanceof chatRepo.ConfirmationExpiredError) return;
  if (err instanceof chatRepo.ConfirmationSubmitError && err.status === 404) {
    // A breadcrumb rather than a console line: it costs nothing on the expected
    // path, survives into whatever error IS captured later in the session, and
    // keeps the browser console clean for the failures that matter.
    Sentry.addBreadcrumb({
      category: "chat-tool-confirmation",
      level: "debug",
      message: "teardown deny found nothing waiting",
      data: { questionId },
    });
    return;
  }
  console.error("Chat tool confirmation: teardown deny failed", { questionId, err });
  Sentry.captureException(err instanceof Error ? err : new Error(String(err)), {
    level: "error",
    tags: { feature: "chat-tool-confirmation", stage: "teardown-deny" },
    extra: { questionId },
    fingerprint: ["chat-tool-confirmation", "teardown-deny"],
  });
}

/**
 * Answer a card with nobody left in front of it. Deliberately un-awaited —
 * every caller is a path the user has already walked away from — and reported
 * rather than swallowed, because a deny that never lands is a tool coroutine
 * holding the session's conversation lock until the HITL timeout.
 */
function denyToolConfirmation(questionId: string, route: string | null, signal: AbortSignal): void {
  void chatRepo.submitConfirmation(questionId, false, { route, signal }).catch((err) => {
    if (!signal.aborted) reportConfirmationDenyFailure(questionId, err);
  });
}

/** WHA-3829: the project a chat is scoped to ("Work in a project"). */
export interface ChatProjectScope {
  id: string;
  name: string;
}

function buildSessionPrewarmKey(sessionId: string, webSearch = false, projectId?: string): string {
  return `${sessionId}${webSearch ? "::ws" : ""}${projectId ? `::p${projectId}` : ""}`;
}

/**
 * Maximized-chat geometry: 90% of the viewport space right of the nav sidebar,
 * centered in it. Shared by setExpanded and the window-resize re-layout so the
 * two can never disagree (WHA-2390 / WHA-3109).
 */
function expandedChatLayout(navSidebarWidth: number): {
  size: { width: number; height: number };
  position: { x: number; y: number };
} {
  const TOP_OFFSET = 56;
  const BOTTOM_MARGIN = 12;
  // The portal spans the full viewport width; the nav sidebar occupies the
  // first navSidebarWidth px, so available space starts after it.
  const availableWidth = document.documentElement.clientWidth - navSidebarWidth;
  const availableHeight = window.innerHeight - TOP_OFFSET - BOTTOM_MARGIN;
  const width = Math.round(availableWidth * 0.9);
  const height = Math.round(availableHeight * 0.9);
  return {
    size: { width, height },
    position: {
      x: navSidebarWidth + Math.round((availableWidth - width) / 2),
      y: Math.round((availableHeight - height) / 2),
    },
  };
}

export const ChatStore = types
  .model("ChatStore", {
    threads: types.map(ChatThread),
    isLoading: types.optional(types.boolean, false),
    statusMessage: types.maybeNull(types.string),
    error: types.maybeNull(types.string),
    sessionId: types.maybeNull(types.string),
    sessionLoading: types.optional(types.boolean, false),
    isCollapsed: types.optional(types.boolean, true),
    // Attached documents for chat context
    attachedDocuments: types.array(AttachedDocument),
    // Attached PM issues for chat context (WHA-3336)
    attachedIssues: types.array(AttachedIssue),
    // Multi-session support (WHA-1465)
    sessions: types.map(ChatSession),
    activeSessions: types.map(types.string), // buildingId → sessionId
    sessionsLoading: types.optional(types.boolean, false),
  })
  .volatile(() => ({
    authAbortController: new AbortController(),
    /** Per-thread loading state (WHA-1981) — uses observable.map for MobX reactivity */
    threadLoadingState: observable.map<
      string,
      {
        isLoading: boolean;
        statusMessage: string | null;
        /** Catalog key + params behind `statusMessage`, when the server sent
         *  them. The status pill's file icon keys off this instead of
         *  regex-parsing localised prose. */
        statusMeta: { key?: string | null; params?: Record<string, unknown> | null } | null;
        intermediateOutput: string | null;
        /** Accumulated `text_delta` SSE text — the final answer streaming in.
         *  Advisory only: the terminal `complete` event carries the
         *  authoritative (scrubbed, citation-validated) message. */
        streamingDraft: string | null;
      }
    >(),
    /** Widget position on screen (WHA-2252) — default: bottom-left, right of nav sidebar */
    widgetPosition: { x: 72, y: -1 },
    /**
     * Widget size (WHA-2252). Width controls the open panel, lane and toasts;
     * the collapsed launcher uses its own compact width.
     */
    widgetSize: { width: CORNER_WIDTH, height: 520 },
    /** Whether the chat is in expanded/maximized mode (WHA-2390) */
    isExpanded: false,
    /** Saved size before expanding, restored on collapse (WHA-2390) */
    preExpandSize: null as { width: number; height: number } | null,
    /** Saved position before expanding, restored on collapse (WHA-2390) */
    preExpandPosition: null as { x: number; y: number } | null,
    /** Nav sidebar width the expanded layout was computed against — reused on window resize */
    expandedNavWidth: 64,
    /** WHA-2716: Per-session web search toggle */
    sessionWebSearch: observable.map<string, boolean>(),
    /**
     * Web search toggle for a building before its first chat session exists,
     * keyed by buildingId. Lets the toggle render and work during the very
     * first chat; seeded into the session on creation, then cleared.
     */
    pendingWebSearch: observable.map<string, boolean>(),
    /** WHA-3829: Per-session "Work in a project" scope ({id, name}). */
    sessionProject: observable.map<string, ChatProjectScope>(),
    /**
     * Project scope for a building before its first chat session exists,
     * keyed by buildingId — same pre-session pattern as pendingWebSearch.
     */
    pendingProject: observable.map<string, ChatProjectScope>(),
    /** Per-session/config Claude prewarm state for historical chats. */
    sessionPrewarmStates: observable.map<string, SessionPrewarmState>(),
    /** WHA-2873: Pending AskUserQuestion from the SDK (mid-stream) */
    pendingAskUser: null as {
      questionId: string;
      questions: Array<{
        question: string;
        header?: string;
        options: Array<{ label: string; description?: string }>;
        multiSelect?: boolean;
      }>;
    } | null,
    /**
     * WHA-3058: tool confirmations awaiting the user, per thread, in the order
     * the server raised them.
     *
     * A QUEUE, not a slot, and keyed by thread. One turn routinely raises a
     * card per write-tool call — twelve `create_pm_issue` cards from a single
     * "create tasks from this protocol". A single slot kept only the last, so
     * eleven backend coroutines waited out their timeout and the chat never
     * finished. A single GLOBAL slot additionally let any thread's stream
     * ending wipe a card another thread was still showing.
     */
    pendingToolConfirmations: observable.map<string, PendingToolConfirmation[]>(),
    /** Per-thread submit state for the card at the head of that queue. */
    toolConfirmationSubmissions: observable.map<string, ToolConfirmationSubmission>(),
    /**
     * Question ids a teardown wanted to deny but could not, because the user's
     * own answer for them was already in flight. Whoever wins, the backend
     * ends up with exactly one answer: if that POST lands the question is
     * resolved, and if it fails the submit path denies on the teardown's
     * behalf — otherwise nothing is left holding the card and the coroutine
     * waits out the HITL timeout with the session's lock. Not observable:
     * nothing renders from it.
     */
    toolConfirmationDenyOnFailure: new Set<string>(),
    /** WHA-3109: True once the user has dragged the widget — disables sidebar-relative auto-positioning. */
    hasUserMovedWidget: false,
    /**
     * WHA-3330: queued user submit that is waiting for one or more attached
     * documents to finish extracting. The user message is added to the thread
     * optimistically the moment they hit Send (so they see it in the chat),
     * but the actual SSE dispatch is deferred until every chip leaves the
     * pre-extraction state. The state-sync hook (useChatbotAttachmentStateSync)
     * watches this field + the chip states and calls `flushQueuedSend()` as
     * soon as the gate clears. Cleared on flush.
     */
    queuedSend: null as {
      params: {
        threadId: string;
        message: string;
        userId: string;
        attachedSourceFilenames?: string[];
        userText?: string;
        analytics?: AssistantSendAnalytics;
      };
      /** Doc ids that were attached at submit time. Used by the flush
       *  reaction to know which chips to wait on. */
      attachedDocIds: string[];
      /** Epoch ms at which the send goes out whatever the chips say. */
      sendBy: number;
      authUserId: string;
      authEpoch: number;
      optimisticMessage: Instance<typeof ChatMessage>;
    } | null,
    /**
     * WHA-3330: doc ids that are currently in a pre-extraction pipeline
     * state. Populated by the state-sync hook from any subscription
     * (input chips, queued send). Used by user-message chip rendering
     * (DocChipGroup) to show the loader on chips inside the message
     * bubble — which is where they move once the user hits Send.
     */
    loadingDocIds: observable.set<string>(),
    /**
     * Messages typed while Franklin was still answering, per thread, oldest
     * first. A turn already in flight no longer blocks the composer: Enter
     * parks the message here and the flush reaction dispatches it the moment
     * the stream ends.
     *
     * Deliberately NOT `queuedSend` above. That slot belongs to the
     * attachment-extraction defer and holds exactly one message; sharing it
     * would let a user who queues a follow-up while a chip is still extracting
     * lose one of the two.
     */
    pendingSends: observable.map<string, PendingSend[]>(),
  }))
  .views((self) => ({
    getToolConfirmationSubmission(threadId: string): ToolConfirmationSubmission | null {
      const submission = self.toolConfirmationSubmissions.get(threadId);
      if (!submission) return null;
      const head = self.pendingToolConfirmations.get(threadId)?.[0];
      return head && head.questionId === submission.questionId ? submission : null;
    },
  }))
  .views((self) => ({
    /** Backward-compat computed view: maps isCollapsed/isExpanded to a ViewMode string */
    get viewMode(): ViewMode {
      if (self.isCollapsed) return "collapsed";
      if (self.isExpanded) return "expanded";
      return "sidebar";
    },
    getThread(threadId: string) {
      return self.threads.get(threadId);
    },
    getMessages(threadId: string) {
      const thread = self.threads.get(threadId);
      return thread ? thread.messages.slice() : [];
    },
    isThreadHydrated(threadId: string) {
      const thread = self.threads.get(threadId);
      return thread?.isHydrated ?? false;
    },

    /**
     * WHA-3330: whether a `documents.state` value indicates the doc is
     * still in the pre-extraction window. Mirrors the chip spinner
     * predicate in SidebarChatInput so the optimistic-send queue uses
     * the exact same gate. EXTRACTING etc. → pending; EXTRACTED + later
     * → ready.
     */
    isPreExtractionState(state: string | null | undefined): boolean {
      if (state == null) return false; // legacy attachments treated as ready
      return PRE_EXTRACTION_STATES.has(state);
    },

    /** WHA-3330: true while the given doc id is still being extracted.
     *  Read by user-message DocChipGroup to show a spinner on the chip
     *  that was just sent along with the user's question. */
    isDocLoading(docId: string): boolean {
      return self.loadingDocIds.has(docId);
    },
    // Multi-session views (WHA-1465)

    /**
     * Get all sessions for a building, sorted by lastMessageAt DESC
     */
    getSessionsForBuilding(buildingId: string) {
      return Array.from(self.sessions.values())
        .filter((s) => s.buildingId === buildingId)
        .sort((a, b) => {
          // Sort by lastMessageAt DESC, then createdAt DESC
          const aTime = a.lastMessageAt || a.createdAt;
          const bTime = b.lastMessageAt || b.createdAt;
          return bTime.localeCompare(aTime);
        });
    },

    /**
     * Get the active session ID for a building
     */
    getActiveSessionId(buildingId: string): string | undefined {
      return self.activeSessions.get(buildingId);
    },

    /**
     * Get the active session for a building
     */
    getActiveSession(buildingId: string) {
      const sessionId = self.activeSessions.get(buildingId);
      return sessionId ? self.sessions.get(sessionId) : undefined;
    },

    /**
     * The confirmation card this thread should be showing, and how many are
     * waiting in total — the card renders "12 requests waiting" from `total`.
     *
     * Deliberately NOT an "N of M" position. Answered cards leave the queue,
     * so the head is always index 0: the position half could only ever read
     * "1 of 12" then "1 of 11", which tells the user nothing about where they
     * are. A count that falls as they work is the honest half.
     */
    getToolConfirmation(threadId: string): {
      entry: PendingToolConfirmation;
      total: number;
    } | null {
      const queue = self.pendingToolConfirmations.get(threadId);
      if (!queue || queue.length === 0) return null;
      return { entry: queue[0], total: queue.length };
    },

    /**
     * The card this thread is holding purely because its own answer settled
     * badly: expired (the backend has stopped waiting, so nothing can be
     * answered, denied or retried — only read and dismissed) or a submit that
     * failed and is showing a Try again.
     *
     * Neither is outstanding for a teardown. Denying and dropping them there
     * would silently turn the user's failed Approve into a Deny and delete the
     * retry the card is offering while they are still looking at it. A newly
     * raised question retires whichever is left.
     */
    settledToolConfirmation(threadId: string): ToolConfirmationSubmission | null {
      // Head-filtered, like every other read of this slot: a state whose
      // question has left the queue belongs to nobody, and reporting it as the
      // thread's settled card would protect a card that is not there from a
      // teardown's deny while the real head is still waiting on one.
      const submission = self.getToolConfirmationSubmission(threadId);
      return submission?.state === "failed" ? submission : null;
    },

    /**
     * Get per-thread loading state (WHA-1981)
     */
    getThreadIsLoading(threadId: string): boolean {
      return self.threadLoadingState.get(threadId)?.isLoading ?? false;
    },

    /**
     * Whether the thread already owns a dispatch: a stream in flight, or a
     * send deferred behind an attachment that is still extracting
     * (`queuedSend`). Both mean a new message has to be parked in
     * `pendingSends` — sending around the deferred one would put the two
     * turns into the thread in the wrong order.
     */
    isThreadBusy(threadId: string): boolean {
      if (self.threadLoadingState.get(threadId)?.isLoading) return true;
      return self.queuedSend?.params.threadId === threadId;
    },

    /**
     * Get per-thread status message (WHA-1981)
     */
    getThreadStatusMessage(threadId: string): string | null {
      return self.threadLoadingState.get(threadId)?.statusMessage ?? null;
    },

    /**
     * Catalog key + params behind the current per-thread status, if any.
     */
    getThreadStatusMeta(
      threadId: string
    ): { key?: string | null; params?: Record<string, unknown> | null } | null {
      return self.threadLoadingState.get(threadId)?.statusMeta ?? null;
    },

    /**
     * Get per-thread live intermediate assistant output.
     */
    getThreadIntermediateOutput(threadId: string): string | null {
      return self.threadLoadingState.get(threadId)?.intermediateOutput ?? null;
    },

    /**
     * Messages typed while this thread's turn was still running, oldest first.
     * Rendered as pending bubbles under the conversation.
     */
    getPendingSends(threadId: string | null): PendingSend[] {
      if (!threadId) return [];
      return self.pendingSends.get(threadId) ?? [];
    },

    /** The message the flush should send next, without removing it. */
    peekPendingSend(threadId: string): PendingSend | null {
      return self.pendingSends.get(threadId)?.[0] ?? null;
    },

    /**
     * Whether the head of the thread's queue can be sent: a message waits for
     * every upload it carries, and one whose upload failed must go back to
     * the user rather than out without the file.
     */
    getPendingSendReadiness(threadId: string | null): PendingSendReadiness {
      const head = threadId ? self.pendingSends.get(threadId)?.[0] : undefined;
      const states = head?.attachments.documents.map((doc) => doc.state) ?? [];
      if (states.includes(UPLOAD_FAILED_STATE)) return "uploadFailed";
      if (states.includes(UPLOADING_STATE)) return "uploading";
      return "ready";
    },

    /**
     * Get per-thread live streaming draft (accumulated text_delta events).
     */
    getThreadStreamingDraft(threadId: string): string | null {
      return self.threadLoadingState.get(threadId)?.streamingDraft ?? null;
    },

    getSessionWebSearch(sessionId: string): boolean {
      return self.sessionWebSearch.get(sessionId) ?? false;
    },
    /**
     * Web search state for a building's chat input. Resolves the active
     * session's toggle when one exists, otherwise the pending pre-session
     * preference — so the toggle works during the very first chat too.
     */
    getWebSearchForBuilding(buildingId: string): boolean {
      const sessionId = self.activeSessions.get(buildingId);
      if (sessionId) {
        return self.sessionWebSearch.get(sessionId) ?? false;
      }
      return self.pendingWebSearch.get(buildingId) ?? false;
    },
    /** WHA-3829: project scope of a specific session, or null. */
    getSessionProject(sessionId: string): ChatProjectScope | null {
      return self.sessionProject.get(sessionId) ?? null;
    },
    /**
     * Project scope for a building's chat input. Resolves the active
     * session's scope when one exists, otherwise the pending pre-session
     * choice — same resolution order as getWebSearchForBuilding.
     */
    getProjectForBuilding(buildingId: string): ChatProjectScope | null {
      const sessionId = self.activeSessions.get(buildingId);
      if (sessionId) {
        return self.sessionProject.get(sessionId) ?? null;
      }
      return self.pendingProject.get(buildingId) ?? null;
    },
    getSessionPrewarmState(
      sessionId: string,
      webSearch = false,
      projectId?: string
    ): SessionPrewarmState | "idle" {
      return (
        self.sessionPrewarmStates.get(buildSessionPrewarmKey(sessionId, webSearch, projectId)) ??
        "idle"
      );
    },
  }))
  .actions((self) => ({
    setWebSearch(sessionId: string, enabled: boolean) {
      self.sessionWebSearch.set(sessionId, enabled);
      try {
        const obj: Record<string, boolean> = {};
        self.sessionWebSearch.forEach((v, k) => {
          obj[k] = v;
        });
        localStorage.setItem("chat:sessionWebSearch", JSON.stringify(obj));
      } catch {
        // localStorage may be unavailable
      }
    },
    setProject(sessionId: string, project: ChatProjectScope | null) {
      if (project) {
        self.sessionProject.set(sessionId, project);
      } else {
        self.sessionProject.delete(sessionId);
      }
      try {
        const obj: Record<string, ChatProjectScope> = {};
        self.sessionProject.forEach((v, k) => {
          obj[k] = v;
        });
        localStorage.setItem("chat:sessionProject", JSON.stringify(obj));
      } catch {
        // localStorage may be unavailable
      }
    },
  }))
  // Expose setStatusMessage as a proper MST action so it can be called
  // from async callbacks (outside flow action context).
  .actions((self) => ({
    setStatusMessage(message: string | null) {
      self.statusMessage = message;
    },
    /** WHA-2873: Set pending AskUserQuestion state (must be an action for MobX reactivity) */
    setPendingAskUser(
      value: {
        questionId: string;
        questions: Array<{
          question: string;
          header?: string;
          options: Array<{ label: string; description?: string }>;
          multiSelect?: boolean;
        }>;
      } | null
    ) {
      self.pendingAskUser = value;
    },
    /**
     * WHA-3058: enqueue a confirmation the server just raised.
     *
     * Appends — never replaces. Overwriting is the whole bug: the card that
     * was displaced could no longer be answered by anyone, while its coroutine
     * kept the SSE stream open.
     */
    enqueueToolConfirmation(threadId: string, entry: PendingToolConfirmation) {
      const queue = self.pendingToolConfirmations.get(threadId) ?? [];
      if (queue.some((e) => e.questionId === entry.questionId)) return;
      // A live question supersedes a settled card the user never got round to
      // acknowledging — an expired notice, or an answer that failed and has
      // been sitting behind a Try again since the last turn. Either is only
      // kept so the click that produced it has an explanation; letting one sit
      // at the head of the queue would make a dead question block a real one.
      const settled = self.settledToolConfirmation(threadId);
      const live = settled ? queue.filter((e) => e.questionId !== settled.questionId) : queue;
      if (live.length !== queue.length) {
        // Only the expired one is known to have nobody waiting on it. A card
        // retired with its answer still unlanded may have a coroutine blocked
        // in `wait_for_confirmation`, so it gets the same best-effort deny a
        // teardown or a Dismiss would have sent.
        if (settled?.errorKey !== "expired") {
          const retired = queue.find((e) => e.questionId === settled?.questionId);
          if (retired)
            denyToolConfirmation(
              retired.questionId,
              retired.server,
              self.authAbortController.signal
            );
        }
        self.toolConfirmationSubmissions.delete(threadId);
      }
      self.pendingToolConfirmations.set(threadId, [...live, entry]);
    },
    /** Drop specific confirmations (answered, or resolved server-side). */
    removeToolConfirmations(threadId: string, questionIds: string[]) {
      const queue = self.pendingToolConfirmations.get(threadId);
      if (!queue) return;
      const drop = new Set(questionIds);
      const kept = queue.filter((e) => !drop.has(e.questionId));
      if (kept.length === queue.length) return;
      if (kept.length === 0) {
        self.pendingToolConfirmations.delete(threadId);
      } else {
        self.pendingToolConfirmations.set(threadId, kept);
      }
      // The submit state goes with its question. Left behind it is a `failed`
      // slot nobody can dismiss — every read of it is head-filtered — that
      // survives until some later submit happens to overwrite it.
      if (drop.has(self.toolConfirmationSubmissions.get(threadId)?.questionId ?? "")) {
        self.toolConfirmationSubmissions.delete(threadId);
      }
    },
    /**
     * Deny everything this thread is still holding, then forget it. The only
     * way a thread's cards go away unanswered — Stop, and the stream's own
     * teardown, both route through here.
     *
     * Aborting our fetch says nothing to the backend, whose tool coroutines
     * would keep waiting on a decision nobody is going to make — holding the
     * session's conversation lock until the 30-minute HITL timeout.
     */
    denyOutstandingToolConfirmations(threadId: string) {
      const queue = self.pendingToolConfirmations.get(threadId);
      const submission = self.toolConfirmationSubmissions.get(threadId);
      // A card the user has already answered, whose answer settled badly, is
      // not outstanding. Expired, the backend stopped waiting and a deny would
      // only 404. Failed, the answer they gave is still theirs to land: denying
      // it here turns their Approve into a Deny, and taking it off screen
      // deletes the retry it is offering — usually seconds after they clicked,
      // since a teardown follows the failure closely. Either way the card is
      // the only thing explaining what happened, so it stays until dismissed
      // (which denies, if anything may still be waiting) or until a new
      // question needs the queue (`enqueueToolConfirmation`, same rule).
      const settledId = self.settledToolConfirmation(threadId)?.questionId ?? null;
      for (const entry of queue ?? []) {
        if (entry.questionId === settledId) continue;
        // A card whose own answer is still in flight is left to that request.
        // Denying it too races the user's approve for the same question_id,
        // and the loser of that race decides — a Stop pressed a beat after
        // Approve must not reverse the approval. Handing it over is only safe
        // because the submit path picks up this marker: if that POST fails,
        // it denies rather than leaving a coroutine nobody can reach.
        if (submission?.state === "submitting" && submission.questionId === entry.questionId) {
          self.toolConfirmationDenyOnFailure.add(entry.questionId);
          continue;
        }
        denyToolConfirmation(entry.questionId, entry.server, self.authAbortController.signal);
      }
      const kept = (queue ?? []).filter((entry) => entry.questionId === settledId);
      if (kept.length > 0) {
        // The kept card keeps its submission: that state IS its message.
        self.pendingToolConfirmations.set(threadId, kept);
      } else {
        self.pendingToolConfirmations.delete(threadId);
        self.toolConfirmationSubmissions.delete(threadId);
      }
    },
    /**
     * Retire a card the user has read the outcome of: an expired one, which
     * stays on screen carrying "Franklin has stopped waiting" until it is
     * acknowledged, or one whose answer failed and may never land — an
     * unpinned route round-robins, and a replica that never held the question
     * 404s every retry. Without this the card sits at the head of the thread's
     * queue behind a "Try again" that cannot work, blocking every sibling.
     * Removing a card the moment the POST 404s, on the other hand, made it
     * vanish with no explanation, which reads as the click having done nothing.
     */
    dismissToolConfirmation(threadId: string, questionId: string) {
      const queue = self.pendingToolConfirmations.get(threadId);
      if (!queue) return;
      const entry = queue.find((e) => e.questionId === questionId);
      const submission = self.toolConfirmationSubmissions.get(threadId);
      // Only an EXPIRED card is known to have nobody waiting on it. A card
      // dismissed after a failed submit may still have a coroutine blocked in
      // `wait_for_confirmation` holding the session's conversation lock, so it
      // gets the same best-effort deny a teardown would have sent.
      if (
        entry &&
        submission?.questionId === questionId &&
        submission.state === "failed" &&
        submission.errorKey !== "expired"
      ) {
        denyToolConfirmation(questionId, entry.server, self.authAbortController.signal);
      }
      const kept = queue.filter((e) => e.questionId !== questionId);
      if (kept.length === 0) {
        self.pendingToolConfirmations.delete(threadId);
      } else {
        self.pendingToolConfirmations.set(threadId, kept);
      }
      if (submission?.questionId === questionId) {
        self.toolConfirmationSubmissions.delete(threadId);
      }
    },
    /**
     * Unpin a card from the pod that raised it, after answers for it failed.
     * `?route=` is only ever an optimisation — a pod that has been replaced
     * takes the ingress's 404 or, just as often, the proxy's 502/504, and
     * either way every retry that kept resending the dead route would fail
     * identically however many times the user pressed Try again. Routeless
     * round-robins, which resolves nothing on a replica that is not holding
     * the waiter, but can at least land.
     */
    clearToolConfirmationRoute(threadId: string, questionId: string) {
      const queue = self.pendingToolConfirmations.get(threadId);
      if (!queue) return;
      const index = queue.findIndex((entry) => entry.questionId === questionId);
      if (index === -1 || queue[index].server === null) return;
      const next = [...queue];
      next[index] = { ...next[index], server: null };
      self.pendingToolConfirmations.set(threadId, next);
    },
    setToolConfirmationSubmission(threadId: string, value: ToolConfirmationSubmission | null) {
      if (value) {
        self.toolConfirmationSubmissions.set(threadId, value);
      } else {
        self.toolConfirmationSubmissions.delete(threadId);
      }
    },
    /**
     * Record how a submit ended — but only while the slot still belongs to the
     * question that submit was for. There is one submission per thread, and a
     * question can leave the queue mid-flight (a server-side release, a
     * `tool_confirmation_resolved`); the user is then looking at the next card
     * and may already have answered it. Writing this outcome regardless would
     * clear that newer card's "submitting" state and re-enable its buttons
     * while its own POST is still in the air.
     */
    settleToolConfirmationSubmission(
      threadId: string,
      questionId: string,
      value: ToolConfirmationSubmission | null
    ) {
      if (self.toolConfirmationSubmissions.get(threadId)?.questionId !== questionId) return;
      if (value) {
        self.toolConfirmationSubmissions.set(threadId, value);
      } else {
        self.toolConfirmationSubmissions.delete(threadId);
      }
    },
    /**
     * Set per-thread loading state (WHA-1981)
     */
    setThreadLoadingState(
      threadId: string,
      isLoading: boolean,
      statusMessage: string | null = null,
      intermediateOutput: string | null = null
    ) {
      if (isLoading) {
        self.threadLoadingState.set(threadId, {
          isLoading,
          statusMessage,
          statusMeta: null,
          intermediateOutput,
          streamingDraft: null,
        });
      } else {
        self.threadLoadingState.delete(threadId);
      }
    },
    /**
     * Set per-thread status message (WHA-1981)
     */
    setThreadStatusMessage(
      threadId: string,
      message: string | null,
      meta: { key?: string | null; params?: Record<string, unknown> | null } | null = null
    ) {
      const state = self.threadLoadingState.get(threadId);
      if (state) {
        self.threadLoadingState.set(threadId, {
          ...state,
          statusMessage: message,
          // Cleared together with the message: a stale key would keep the old
          // icon next to the next status.
          statusMeta: message ? meta : null,
        });
      }
    },
    /**
     * Set per-thread live intermediate assistant output.
     */
    setThreadIntermediateOutput(threadId: string, message: string | null) {
      const state = self.threadLoadingState.get(threadId);
      if (state) {
        self.threadLoadingState.set(threadId, { ...state, intermediateOutput: message });
      }
    },
    /**
     * Append streamed text_delta content to the thread's live draft.
     */
    appendThreadStreamingDraft(threadId: string, text: string) {
      const state = self.threadLoadingState.get(threadId);
      if (state) {
        self.threadLoadingState.set(threadId, {
          ...state,
          streamingDraft: (state.streamingDraft ?? "") + text,
        });
      }
    },
    /**
     * Drop the thread's live streaming draft (superseded by an
     * intermediate_output flush or the terminal complete event).
     */
    clearThreadStreamingDraft(threadId: string) {
      const state = self.threadLoadingState.get(threadId);
      if (state && state.streamingDraft !== null) {
        self.threadLoadingState.set(threadId, { ...state, streamingDraft: null });
      }
    },
    clearBuildingChatState(buildingId: string) {
      self.activeSessions.delete(buildingId);
      // Clear stale messages from any threads belonging to this building
      // so legacy thread cache doesn't bleed through during sessionsLoading
      self.threads.forEach((thread) => {
        if (thread.buildingId === buildingId) {
          thread.messages.clear();
          thread.isHydrated = false;
        }
      });
    },
    /**
     * Set the web search toggle for a building. Targets the active session
     * when one exists, otherwise stores a pending preference (persisted to
     * localStorage) that is seeded into the first session on creation — so
     * the toggle is usable during the very first chat, before any session.
     */
    setWebSearchForBuilding(buildingId: string, enabled: boolean) {
      const sessionId = self.activeSessions.get(buildingId);
      if (sessionId) {
        self.setWebSearch(sessionId, enabled);
        return;
      }
      self.pendingWebSearch.set(buildingId, enabled);
      try {
        const obj: Record<string, boolean> = {};
        self.pendingWebSearch.forEach((v, k) => {
          obj[k] = v;
        });
        localStorage.setItem("chat:pendingWebSearch", JSON.stringify(obj));
      } catch {
        // localStorage may be unavailable
      }
    },
    /**
     * Move a building's pending pre-session web search preference onto a newly
     * created session, then clear the pending entry. Called on createSession so
     * the toggle the user set before the first chat existed takes effect. No-op
     * when nothing is pending.
     */
    consumePendingWebSearch(buildingId: string, sessionId: string) {
      const pending = self.pendingWebSearch.get(buildingId);
      if (pending === undefined) {
        return;
      }
      self.pendingWebSearch.delete(buildingId);
      try {
        const obj: Record<string, boolean> = {};
        self.pendingWebSearch.forEach((v, k) => {
          obj[k] = v;
        });
        localStorage.setItem("chat:pendingWebSearch", JSON.stringify(obj));
      } catch {
        // localStorage may be unavailable
      }
      self.setWebSearch(sessionId, pending);
    },
    /**
     * WHA-3829: Set (or clear with null) the project scope for a building.
     * Targets the active session when one exists, otherwise stores a
     * pending pre-session choice — same pattern as setWebSearchForBuilding.
     */
    setProjectForBuilding(buildingId: string, project: ChatProjectScope | null) {
      const sessionId = self.activeSessions.get(buildingId);
      if (sessionId) {
        self.setProject(sessionId, project);
        return;
      }
      if (project) {
        self.pendingProject.set(buildingId, project);
      } else {
        self.pendingProject.delete(buildingId);
      }
      try {
        const obj: Record<string, ChatProjectScope> = {};
        self.pendingProject.forEach((v, k) => {
          obj[k] = v;
        });
        localStorage.setItem("chat:pendingProject", JSON.stringify(obj));
      } catch {
        // localStorage may be unavailable
      }
    },
    /**
     * WHA-3829: Move a building's pending pre-session project scope onto a
     * newly created session, then clear the pending entry. No-op when
     * nothing is pending.
     */
    consumePendingProject(buildingId: string, sessionId: string) {
      const pending = self.pendingProject.get(buildingId);
      if (pending === undefined) {
        return;
      }
      self.pendingProject.delete(buildingId);
      try {
        const obj: Record<string, ChatProjectScope> = {};
        self.pendingProject.forEach((v, k) => {
          obj[k] = v;
        });
        localStorage.setItem("chat:pendingProject", JSON.stringify(obj));
      } catch {
        // localStorage may be unavailable
      }
      self.setProject(sessionId, pending);
    },
  }))
  .actions((self) => ({
    removeAttachedDocument(docId: string) {
      const idx = self.attachedDocuments.findIndex((d) => d.id === docId);
      if (idx >= 0) self.attachedDocuments.splice(idx, 1);
    },
    clearAttachedIssues() {
      self.attachedIssues.clear();
    },
    loadSessionMessages: flow(function* loadSessionMessages(params: {
      sessionId: string;
      threadId: string;
      buildingId: string;
      orgId: string;
      userId: string;
      limit?: number;
      offset?: number;
    }) {
      const { signal } = self.authAbortController;
      self.isLoading = true;
      self.error = null;

      try {
        const result: chatRepo.SessionMessagesResponse = yield chatRepo.fetchSessionMessages(
          params.sessionId,
          params.limit ?? 50,
          params.offset ?? 0,
          signal
        );
        signal.throwIfAborted();

        // Staleness guard: if the user switched sessions while we were
        // fetching, don't overwrite the thread — the new session's data
        // takes priority. (WHA-2859)
        const currentActiveId = self.activeSessions.get(params.buildingId);
        if (currentActiveId && currentActiveId !== params.sessionId) {
          return result;
        }

        // Get or create thread for this session
        const threadId = params.threadId;
        let thread = self.threads.get(threadId);

        if (!thread) {
          thread = ChatThread.create({
            threadId,
            buildingId: params.buildingId,
            orgId: params.orgId,
            sessionId: params.sessionId,
            messages: [],
            isHydrated: false,
          });
          self.threads.set(threadId, thread);
        }

        // Replace messages (offset=0) or append (offset>0)
        if ((params.offset ?? 0) === 0) {
          thread.messages.replace(
            result.messages.map((m) =>
              ChatMessage.create({
                id: m.id,
                role: m.role as "user" | "assistant",
                content: m.content,
                sourceFilenames: m.sourceFilenames ?? [],
                choices: m.choices ?? [],
                choiceQuestion: m.choiceQuestion ?? "",
                usedDocumentTool: m.usedDocumentTool ?? false,
                ehrLink: m.ehrLink ?? null,
              })
            )
          );
        } else {
          // Prepend older messages (pagination loads older messages)
          const existingMessages = thread.messages.slice();
          thread.messages.replace([
            ...result.messages.map((m) =>
              ChatMessage.create({
                id: m.id,
                role: m.role as "user" | "assistant",
                content: m.content,
                sourceFilenames: m.sourceFilenames ?? [],
                choices: m.choices ?? [],
                choiceQuestion: m.choiceQuestion ?? "",
                usedDocumentTool: m.usedDocumentTool ?? false,
                ehrLink: m.ehrLink ?? null,
              })
            ),
            ...existingMessages,
          ]);
        }

        thread.isHydrated = true;

        return result;
      } catch (e: unknown) {
        if (signal.aborted) throw e;
        const errorMsg = e instanceof Error ? e.message : String(e);
        self.error = errorMsg;
        throw e;
      } finally {
        if (!signal.aborted) self.isLoading = false;
      }
    }),
  }))
  .actions((self) => {
    // Per-thread AbortControllers for cancelling in-flight SSE streams (WHA-1981)
    const streamAbortControllers = new Map<string, AbortController>();
    const prewarmRequests = new Map<string, Promise<chatRepo.PrewarmSessionResponse>>();
    // Sends a held message at its `sendBy`. Kept here rather than in the
    // component that watches the attachments, so the deadline holds while the
    // chat is not on screen.
    let queuedSendTimer: ReturnType<typeof setTimeout> | null = null;
    // Stop watching the documents a forced send went out without.
    const stalledTrackingTimers = new Set<ReturnType<typeof setTimeout>>();

    // Define helper actions as functions in closure so flows can call them
    function addUserMessage(threadId: string, content: string, messageId: string | null = null) {
      const thread = self.threads.get(threadId);
      if (!thread) {
        throw new Error("Thread not initialized");
      }
      const message = ChatMessage.create({
        id: messageId,
        role: "user",
        content,
        sourceFilenames: [],
      });
      thread.messages.push(message);
      return message;
    }

    function addAssistantMessage(
      threadId: string,
      content: string,
      sourceFilenames: string[] = [],
      messageId: string | null = null,
      choices: string[] = [],
      choiceQuestion = "",
      usedDocumentTool = false,
      ehrLink: string | null = null,
      generatedFiles: chatRepo.GeneratedFile[] = []
    ) {
      const thread = self.threads.get(threadId);
      if (!thread) {
        throw new Error("Thread not initialized");
      }
      thread.messages.push(
        ChatMessage.create({
          id: messageId,
          role: "assistant",
          content,
          sourceFilenames: dedupeFilenames(sourceFilenames),
          generatedFiles: generatedFiles.map((f) => ({
            id: f.id,
            filename: f.filename,
            mime: f.mime,
            sizeBytes: f.size_bytes,
            dataB64: f.data_b64,
          })),
          choices,
          choiceQuestion,
          usedDocumentTool,
          ehrLink,
        })
      );
    }

    function isAuthenticated(): boolean {
      const { authStore } = getRoot<ChatRootAccess>(self);
      return Boolean(authStore?.isAuthenticated);
    }

    function currentAuthSession(): { authUserId: string; authEpoch: number } | null {
      const { authStore } = getRoot<ChatRootAccess>(self);
      if (
        !authStore?.isAuthenticated ||
        !authStore.userId ||
        !Number.isInteger(authStore.authEpoch)
      ) {
        return null;
      }
      return { authUserId: authStore.userId, authEpoch: authStore.authEpoch };
    }

    /**
     * Rewrite one document chip on every parked message that carries it.
     * `update` sees that message's documents, and returning null drops the chip.
     */
    function updatePendingAttachment(
      documentId: string,
      update: (
        doc: PendingAttachmentDocument,
        documents: PendingAttachmentDocument[]
      ) => PendingAttachmentDocument | null
    ) {
      for (const [threadId, queue] of Array.from(self.pendingSends.entries())) {
        if (
          !queue.some((entry) => entry.attachments.documents.some((doc) => doc.id === documentId))
        ) {
          continue;
        }
        self.pendingSends.set(
          threadId,
          queue.map((entry) => ({
            ...entry,
            attachments: {
              ...entry.attachments,
              documents: entry.attachments.documents.flatMap((doc, _, documents) =>
                doc.id === documentId ? (update(doc, documents) ?? []) : [doc]
              ),
            },
          }))
        );
      }
    }

    /**
     * Everything that can refuse a send before the thread is touched. It throws
     * `ChatSendRejectedError`, which promises the caller that nothing was added:
     * no optimistic bubble and no error bubble, so the caller still owns the
     * text and has to say why it was refused. `submitMessage` must run this
     * BEFORE its optimistic add — a refusal after it leaves a user bubble with
     * no answer and no error under it.
     *
     * The length limit counts `userText` (what the user typed), never the
     * attachment headers prepended to it; it defaults to `message` for callers
     * that prepend nothing.
     */
    function assertCanSend(params: { threadId: string; message: string; userText?: string }) {
      if (!params.message.trim()) {
        throw new ChatSendRejectedError("Message cannot be empty");
      }
      assertChatMessageSendable(params.userText ?? params.message);
      const thread = self.threads.get(params.threadId);
      if (!thread) {
        throw new ChatSendRejectedError("Thread not initialized");
      }
      if (!isAuthenticated()) {
        throw new ChatSendRejectedError("Authentication required");
      }
      return thread;
    }

    return {
      addUserMessage,
      addAssistantMessage,

      /**
       * Ensure a thread exists for a given threadId (used for new sessions)
       */
      ensureThread(params: { threadId: string; buildingId: string; orgId: string }) {
        if (self.threads.has(params.threadId)) {
          return self.threads.get(params.threadId)!;
        }

        const thread = ChatThread.create({
          threadId: params.threadId,
          buildingId: params.buildingId,
          orgId: params.orgId,
          sessionId: self.sessionId ?? null,
          messages: [],
          isHydrated: true,
        });

        self.threads.set(params.threadId, thread);
        return thread;
      },

      /**
       * Initialize a chat thread for a specific building
       * On new browser sessions, starts with empty chat (previous chats can be loaded separately)
       */
      initThread(params: { buildingId: string; orgId: string; userId: string }) {
        const threadId = `${params.orgId}-${params.userId}-${params.buildingId}`;

        // Check if thread already exists
        if (self.threads.has(threadId)) {
          return self.threads.get(threadId)!;
        }

        // Create new thread
        const thread = ChatThread.create({
          threadId,
          buildingId: params.buildingId,
          orgId: params.orgId,
          sessionId: self.sessionId ?? null,
          messages: [],
          isHydrated: false,
        });

        self.threads.set(threadId, thread);

        thread.isHydrated = true;
        return thread;
      },

      /**
       * Send a message to the chatbot and get a response
       * Automatically adds both user and assistant messages to the thread
       * Returns the response and any generated title for the session
       */
      sendMessage: flow(function* sendMessage(params: {
        threadId: string;
        message: string;
        userId: string;
        attachedSourceFilenames?: string[];
        /** WHA-3330: when called via flushQueuedSend, the optimistic
         *  user message was already added at submitMessage time. Skip
         *  the second add to avoid duplicating it in the thread. */
        skipOptimisticAdd?: boolean;
        /** The part of `message` the user typed — see `assertCanSend`. */
        userText?: string;
        analytics?: AssistantSendAnalytics;
      }): Generator<
        any,
        {
          message: string;
          generatedTitle: string | null;
          assistantMessageId: string | null;
          sourceFilenames: string[];
        },
        any
      > {
        const trimmedMessage = params.message.trim();
        const thread = assertCanSend(params);
        const track = bindTrackToCurrentUser(thread.orgId);
        const exchangeId = crypto.randomUUID();
        let questionReported = false;
        const reportAcceptedQuestion = () => {
          if (questionReported) return;
          questionReported = true;
          track("assistant_message_sent", {
            surface: "sidebar_chat",
            question_text: params.userText ?? trimmedMessage,
            exchange_id: exchangeId,
            thread_id: params.threadId,
            suggestion_used: params.analytics?.suggestionUsed ?? false,
            attachment_count:
              params.analytics?.attachmentCount ?? params.attachedSourceFilenames?.length ?? 0,
          });
        };

        // Abort any previous in-flight stream for THIS thread only (WHA-1981)
        const existingController = streamAbortControllers.get(params.threadId);
        if (existingController) {
          // Everything queued belongs to the turn being superseded — this
          // stream has not raised anything yet — and none of it can be
          // answered once its stream is gone. Denying here rather than in the
          // old flow's `finally` is the only place that works: that teardown
          // sees itself as no longer current and skips, leaving the new turn's
          // card queued behind dead questions the user has to click through.
          self.denyOutstandingToolConfirmations(params.threadId);
          existingController.abort();
        }
        const abortController = new AbortController();
        streamAbortControllers.set(params.threadId, abortController);
        const { signal } = abortController;
        const authSignal = self.authAbortController.signal;

        // Every closure below outlives its own stream: a superseding send for
        // the same thread aborts this one, but this flow's rejection, `finally`
        // and any timer already scheduled still run — after the new stream has
        // taken over the thread's shared state. Anything that writes that state
        // has to check it is still the current stream first, or it prepends the
        // aborted answer's tail to the new draft, or blanks it mid-stream.
        const isCurrentStream = () =>
          streamAbortControllers.get(params.threadId) === abortController;

        // Coalesce text_delta events so the growing draft re-renders (and
        // re-parses its markdown) at most every ~80ms instead of per token.
        let draftBuffer = "";
        let draftFlushTimer: ReturnType<typeof setTimeout> | null = null;
        const flushDraft = () => {
          draftFlushTimer = null;
          if (draftBuffer && isCurrentStream()) {
            const chunk = draftBuffer;
            draftBuffer = "";
            self.appendThreadStreamingDraft(params.threadId, chunk);
          }
        };
        const stopDraftStream = () => {
          if (draftFlushTimer) {
            clearTimeout(draftFlushTimer);
            draftFlushTimer = null;
          }
          draftBuffer = "";
          if (isCurrentStream()) {
            self.clearThreadStreamingDraft(params.threadId);
          }
        };
        // The whole draft as the user sees it: what has been flushed plus what
        // is still waiting on the 80ms timer.
        const streamedSoFar = () =>
          (self.getThreadStreamingDraft(params.threadId) ?? "") + draftBuffer;
        // Every segment this turn has already flushed, oldest first. An
        // `intermediate_output` *replaces* the bubble's content, so the turn
        // has to keep the list: a second flush carrying only its own segment
        // would erase the first from the screen mid-stream, and the draft
        // cannot put it back — the first flush cut it out.
        const flushedSegments: string[] = [];
        // The answer as the user is reading it: the flushed segments (rendered
        // in their own bubble) followed by everything that streamed past them.
        // A turn that ends early keeps this, not the draft alone — the draft
        // has had the flushed segments cut out of it, and the loading-state
        // teardown takes the bubble with it.
        const answerSoFar = () =>
          [...flushedSegments, streamedSoFar()]
            .map((part) => part.trim())
            .filter(Boolean)
            .join("\n\n");
        // Collapse the whitespace a server-side re-render is free to change,
        // so a flush that is the same text as the deltas is still recognised
        // as such.
        const normalizeFlush = (text: string) => text.replace(/\s+/g, " ").trim();
        // An `intermediate_output` flush supersedes the deltas that produced
        // it, but only those: text that has already streamed past the flushed
        // segment belongs to the next one and has to stay on screen, or the
        // answer loses its opening and renders from mid-sentence. Answers
        // whether the draft has given the segment up — only then may the caller
        // record it, or the same text sits in the bubble and in the draft, and
        // an interrupted turn persists both.
        const supersedeDraft = (flushed: string): boolean => {
          const streamed = streamedSoFar().trimStart();
          const segment = flushed.trim();
          if (!segment) {
            return false;
          }
          // Reasoning that never streamed as deltas: nothing to cut, and the
          // flush is the only copy of that text.
          if (!streamed) {
            return true;
          }
          if (streamed.startsWith(segment)) {
            const rest = streamed.slice(segment.length).trimStart();
            stopDraftStream();
            if (rest && isCurrentStream()) {
              self.appendThreadStreamingDraft(params.threadId, rest);
            }
            return true;
          }
          // The flush is built server-side from the same tokens but is not
          // guaranteed byte-identical to them (whitespace normalization,
          // markdown fix-ups, scrubbing). When it renders exactly what has
          // streamed so far, it accounts for all of it.
          if (normalizeFlush(streamed) === normalizeFlush(segment)) {
            stopDraftStream();
            return true;
          }
          // It covers only part of what streamed, in a shape we can't line up
          // with the deltas: leave the draft alone — deleting answer text out
          // from under the user mid-stream is worse — and leave the segment
          // unrecorded, so the draft stays its single copy.
          return false;
        };

        // Set per-thread loading state (WHA-1981)
        self.setThreadLoadingState(params.threadId, true);
        // Keep global state for backward compatibility
        self.isLoading = true;
        self.statusMessage = null;
        self.error = null;

        try {
          // Add user message (call closure function directly). Skipped
          // when called from flushQueuedSend — the message was already
          // optimistically rendered when the user hit Send (WHA-3330).
          if (!params.skipOptimisticAdd) {
            addUserMessage(params.threadId, trimmedMessage);
          }

          const activeSessionId = self.activeSessions.get(thread.buildingId) ?? null;
          const enableWebSearch = self.sessionWebSearch.get(activeSessionId ?? "") ?? false;
          // WHA-3829: active session's project scope, or the pending
          // pre-session choice on the very first message.
          const activeProject = activeSessionId
            ? (self.sessionProject.get(activeSessionId) ?? null)
            : (self.pendingProject.get(thread.buildingId) ?? null);
          const prewarmKey = activeSessionId
            ? buildSessionPrewarmKey(activeSessionId, enableWebSearch, activeProject?.id)
            : null;
          const inFlightPrewarm = prewarmKey ? prewarmRequests.get(prewarmKey) : null;

          if (inFlightPrewarm) {
            try {
              yield withAbort(inFlightPrewarm, signal);
            } catch {
              // Fall back to the normal send path if prewarm fails.
            }
          }

          // Logout can happen while the session is warming. Do not enter the
          // repository (which reads the current token) for an aborted send.
          signal.throwIfAborted();

          // Send to API via SSE streaming
          const result: chatRepo.SendMessageResult = yield chatRepo.sendMessageStream(
            {
              message: trimmedMessage,
              threadId: params.threadId,
              orgId: thread.orgId,
              buildingId: Number(thread.buildingId),
              userId: params.userId,
              enableWebSearch,
              ...(activeProject && { projectId: Number(activeProject.id) }),
            },
            (event: chatRepo.StreamEvent) => {
              if (authSignal.aborted) return;
              reportAcceptedQuestion();
              // A superseded stream keeps dispatching: `sendMessageStream`
              // parses and delivers every event of an already-read chunk
              // synchronously, and the abort only lands at the next
              // `reader.read()`. Those events must not touch thread state the
              // replacement now owns — they would blank the new stream's
              // status or push this turn's reasoning into its bubble. The two
              // window signals below report database work that really
              // happened, so they fire whoever owns the thread now.
              const touchesThreadState =
                event.type !== "building_data_updated" && event.type !== "pm_issue_changed";
              if (touchesThreadState && !isCurrentStream()) {
                return;
              }

              if (event.type === "status") {
                self.setThreadStatusMessage(params.threadId, event.message, {
                  key: event.key ?? null,
                  params: event.params ?? null,
                });
                self.setStatusMessage(event.message);
              } else if (event.type === "text_delta") {
                // The status described work that has now produced text, so it
                // is done — leaving it up would report a finished search under
                // the draft for the rest of the answer. A later tool call
                // sends a fresh status event of its own.
                if (self.getThreadStatusMessage(params.threadId)) {
                  self.setThreadStatusMessage(params.threadId, null);
                  self.setStatusMessage(null);
                }
                draftBuffer += event.text;
                if (!draftFlushTimer) {
                  draftFlushTimer = setTimeout(flushDraft, 80);
                }
              } else if (event.type === "intermediate_output") {
                // The flushed reasoning text supersedes the deltas that
                // produced it — drop that much of the draft so the segment
                // isn't shown twice (once as draft, once as the intermediate
                // bubble), and no more. The bubble then carries every segment
                // the turn has flushed, not just the last one; a flush the
                // draft did not give up is left to the draft alone.
                if (supersedeDraft(event.message)) {
                  flushedSegments.push(event.message.trim());
                }
                self.setThreadIntermediateOutput(
                  params.threadId,
                  flushedSegments.join("\n\n") || null
                );
              } else if (event.type === "ask_user") {
                self.setPendingAskUser({
                  questionId: event.question_id,
                  questions: event.questions,
                });
              } else if (event.type === "tool_confirmation") {
                self.enqueueToolConfirmation(params.threadId, {
                  questionId: event.question_id,
                  payload: event.payload,
                  server: event.server ?? null,
                });
              } else if (event.type === "tool_confirmation_resolved") {
                // The server answered these for us — "Always allow" released
                // the rest of the tool's queue, or the turn was cancelled.
                // Leaving them up would offer buttons that can only 404.
                self.removeToolConfirmations(params.threadId, event.question_ids);
              } else if (event.type === "building_data_updated") {
                if (typeof window !== "undefined") {
                  window.dispatchEvent(
                    new CustomEvent("franklin:building-data-updated", {
                      detail: { attributeKey: event.attribute_key },
                    })
                  );
                }
              } else if (event.type === "pm_issue_changed") {
                // WHA-3301: belt-and-suspenders refresh signal for the PM
                // module. Supabase realtime *should* already broadcast the
                // row to subscribers, but postgres-role inserts can be
                // missed depending on the publication setup. The PM Issues
                // page listens for this window event and reloads.
                if (typeof window !== "undefined") {
                  window.dispatchEvent(
                    new CustomEvent("franklin:pm-issue-changed", {
                      detail: {
                        kind: event.kind,
                        issueNumber: event.issue_number,
                        buildingId: event.building_id,
                      },
                    })
                  );
                }
              }
            },
            signal
          );

          signal.throwIfAborted();

          reportAcceptedQuestion();

          if (prewarmKey) {
            self.sessionPrewarmStates.set(prewarmKey, "ready");
          }

          self.setThreadIntermediateOutput(params.threadId, null);

          // Source 1: backend sources + user attachments
          let sourceFilenames = dedupeFilenames([
            ...(result.sourceFilenames ?? []),
            ...(params.attachedSourceFilenames ?? []),
          ]);

          // Source 2: always scan assistant text for additional filenames
          const candidates = extractFilenameCandidates(result.message);
          const newCandidates = candidates.filter((c) => !sourceFilenames.includes(c));
          if (newCandidates.length > 0) {
            try {
              const validated: string[] = yield getExistingDocumentFilenames(
                thread.buildingId,
                newCandidates
              );
              sourceFilenames = dedupeFilenames([...sourceFilenames, ...validated]);
            } catch (validationError) {
              if (process.env.NODE_ENV === "development") {
                console.warn("Filename fallback validation failed", validationError);
              }
            }
          }

          signal.throwIfAborted();
          // Add assistant response
          track("assistant_response_received", {
            surface: "sidebar_chat",
            question_text: params.userText ?? trimmedMessage,
            answer_text: result.message,
            exchange_id: exchangeId,
            thread_id: params.threadId,
            ...(result.assistantMessageId && { answer_message_id: result.assistantMessageId }),
          });
          addAssistantMessage(
            params.threadId,
            result.message,
            sourceFilenames,
            result.assistantMessageId,
            result.choices ?? [],
            result.choiceQuestion ?? "",
            result.usedDocumentTool,
            result.ehrLink ?? null,
            result.generatedFiles ?? []
          );
          // Only now that the authoritative message is on screen — dropping
          // the draft earlier would blank the streamed answer (and re-show
          // the loading bubble) across the awaited filename validation above.
          stopDraftStream();

          return {
            ...result,
            sourceFilenames,
          };
        } catch (e: unknown) {
          const isAbortError =
            e !== null && typeof e === "object" && "name" in e && e.name === "AbortError";
          if (isAbortError) {
            // A newer send aborted this one: the trailing user message is then
            // the new send's and the draft is its answer — leave both alone.
            const isUserStop = isCurrentStream();
            // What the user was reading when they hit Stop: the flushed
            // opening plus the draft, including the chunk still waiting on the
            // flush timer.
            const partialAnswer = isUserStop ? answerSoFar() : "";

            if (isUserStop && partialAnswer) {
              // Keep the exchange: the `finally` below blanks the draft, so
              // dropping it here (and the question with it) would erase a
              // half-read answer the moment the user asked us to stop writing
              // it. Local-only — the backend never completed this turn, so
              // there is no assistant message id to attach.
              thread.messages.push(
                ChatMessage.create({
                  id: null,
                  role: "assistant",
                  content: partialAnswer,
                  sourceFilenames: [],
                })
              );
            } else if (isUserStop) {
              // Nothing was written yet: remove the optimistic user message so
              // stopping before the answer starts leaves no trace (WHA-2637).
              const lastMsg = thread.messages[thread.messages.length - 1];
              if (lastMsg?.role === "user") {
                thread.messages.splice(thread.messages.length - 1, 1);
              }
            }
            return {
              message: "",
              generatedTitle: null,
              assistantMessageId: null,
              sourceFilenames: [],
            };
          }

          // A superseded stream can fail without an AbortError too: when the
          // backend closes its SSE body server-side the body ends without a
          // `complete` event, which surfaces as a plain Error. Writing the
          // error state then would push an error bubble into the thread the
          // *new* stream is answering — and the `finally` below correctly
          // skips teardown, so it sits there with a live stream running.
          if (!isCurrentStream()) {
            throw e;
          }

          const errorMsg = e instanceof Error ? e.message : String(e);
          self.error = errorMsg;

          // WHA-3829: the backend rejected the project scope (project
          // deleted/hidden mid-conversation, or access revoked). Clear the
          // chip so the next send works; the error message below tells the
          // user what happened.
          if (errorMsg.includes("PROJECT_SCOPE_")) {
            const scopedSessionId = self.activeSessions.get(thread.buildingId);
            if (scopedSessionId) {
              self.setProject(scopedSessionId, null);
            }
            self.setProjectForBuilding(thread.buildingId, null);
          }

          // The body can die after most of the answer has arrived (network
          // drop, an `error` event, a close without `complete`). Keep what was
          // on screen, exactly as the Stop path does — the `finally` blanks the
          // draft, so otherwise a half-read answer is replaced by the error
          // alone. Local-only: this turn never completed server-side.
          const streamedBeforeError = answerSoFar();
          if (streamedBeforeError) {
            thread.messages.push(
              ChatMessage.create({
                id: null,
                role: "assistant",
                content: streamedBeforeError,
                sourceFilenames: [],
              })
            );
          }

          // Add an error assistant message so the user sees inline feedback
          thread.messages.push(
            ChatMessage.create({
              id: null,
              role: "assistant",
              content: errorMsg,
              sourceFilenames: [],
              isError: true,
            })
          );

          throw e;
        } finally {
          // Clean up per-thread state (WHA-1981) — but only the state this
          // stream still owns. A superseded flow's `finally` runs after its
          // replacement has already started, so tearing down unconditionally
          // would blank the new answer, drop its controller and put the loading
          // bubble back mid-stream. `stopDraftStream` carries its own guard so
          // the timer is always cleared.
          const wasCurrentStream = isCurrentStream();
          stopDraftStream();
          if (wasCurrentStream) {
            streamAbortControllers.delete(params.threadId);
            self.setThreadLoadingState(params.threadId, false);
            // Keep global state for backward compatibility
            self.isLoading = false;
            self.statusMessage = null;
            // WHA-2873: Clear any pending question on stream end
            self.setPendingAskUser(null);
            // WHA-3058: only THIS thread's cards. Clearing globally meant one
            // conversation ending wiped the card another was still showing.
            // Deny rather than drop: a stream that ended because the network
            // went away leaves coroutines still waiting on these questions,
            // exactly the hang Stop denies to avoid. When the turn really did
            // finish, the denies 404 harmlessly.
            self.denyOutstandingToolConfirmations(params.threadId);
          }
        }
      }),

      /**
       * Abort the in-flight SSE stream for a thread (WHA-2637).
       */
      stopStream(threadId: string) {
        // Deny BEFORE aborting: the deny is its own fetch, unaffected by this
        // controller, and the backend's teardown cancel is the backstop rather
        // than the mechanism.
        self.denyOutstandingToolConfirmations(threadId);
        const controller = streamAbortControllers.get(threadId);
        if (controller) {
          controller.abort();
        }
      },

      /** Park a message, with the attachments it was sent with, on `threadId`. */
      enqueuePendingSend(
        threadId: string,
        message: string,
        attachments: PendingAttachments = { documents: [], issues: [] },
        fromSuggestion = false
      ) {
        const trimmed = message.trim();
        if (!trimmed) return;
        const entry = {
          id: `pending-${++pendingSendSeq}`,
          text: trimmed,
          attachments,
          fromSuggestion,
        };
        self.pendingSends.set(threadId, [...(self.pendingSends.get(threadId) ?? []), entry]);
      },

      /**
       * Empty the composer's chips and hand them back: Send moves them onto
       * the message, so they cannot stay behind in the composer.
       */
      takeComposerAttachments(): PendingAttachments {
        const documents = self.attachedDocuments.map((doc) => ({
          id: doc.id,
          filename: doc.filename,
          state: doc.state ?? null,
        }));
        const issues = self.attachedIssues.map((issue) => ({
          id: issue.id,
          issue_number: issue.issue_number,
          title: issue.title,
        }));
        self.attachedDocuments.clear();
        self.attachedIssues.clear();
        return { documents, issues };
      },

      /**
       * Put a message's chips back in the composer — its send was refused, so
       * the user gets the files back with the text. Failed uploads stay out.
       */
      restoreComposerAttachments(attachments: PendingAttachments) {
        for (const doc of attachments.documents) {
          if (doc.state === UPLOAD_FAILED_STATE) continue;
          if (!self.attachedDocuments.some((existing) => existing.id === doc.id)) {
            self.attachedDocuments.push(AttachedDocument.create(doc));
          }
        }
        for (const issue of attachments.issues) {
          if (!self.attachedIssues.some((existing) => existing.id === issue.id)) {
            self.attachedIssues.push(AttachedIssue.create(issue));
          }
        }
      },

      /** Show a chip for a file the moment it is picked; returns its placeholder id. */
      addUploadingAttachment(filename: string): string {
        const id = `upload-${++uploadPlaceholderSeq}`;
        self.attachedDocuments.push(
          AttachedDocument.create({ id, filename, state: UPLOADING_STATE })
        );
        return id;
      },

      /**
       * The upload landed: the placeholder becomes the real document, wherever
       * the chip is now — still in the composer, or on a message waiting for it.
       */
      resolveUploadingAttachment(placeholderId: string, documentId: string) {
        const index = self.attachedDocuments.findIndex((doc) => doc.id === placeholderId);
        if (index >= 0) {
          if (self.attachedDocuments.some((doc) => doc.id === documentId)) {
            self.attachedDocuments.splice(index, 1);
          } else {
            self.attachedDocuments[index].id = documentId;
            self.attachedDocuments[index].state = "UNPROCESSED";
          }
        }
        // A message that already carries the document (the same file attached
        // twice) keeps that chip, as the composer does.
        updatePendingAttachment(placeholderId, (doc, documents) =>
          documents.some((existing) => existing.id === documentId)
            ? null
            : { ...doc, id: documentId, state: "UNPROCESSED" }
        );
      },

      /** The upload failed: drop the composer chip, and flag any message carrying it. */
      failUploadingAttachment(placeholderId: string) {
        const index = self.attachedDocuments.findIndex((doc) => doc.id === placeholderId);
        if (index >= 0) self.attachedDocuments.splice(index, 1);
        updatePendingAttachment(placeholderId, (doc) => ({ ...doc, state: UPLOAD_FAILED_STATE }));
      },

      /**
       * Drop one queued message by identity — the ✕ on its bubble, and the
       * flush once its send is confirmed. Nothing leaves the queue on a guess
       * about where it sat.
       */
      removePendingSend(threadId: string, id: string) {
        const queue = self.pendingSends.get(threadId);
        if (!queue) return;
        const rest = queue.filter((entry) => entry.id !== id);
        if (rest.length === 0) self.pendingSends.delete(threadId);
        else if (rest.length !== queue.length) self.pendingSends.set(threadId, rest);
      },

      /** Empty a thread's queue and hand back what was in it, oldest first. */
      takePendingSends(threadId: string): PendingSend[] {
        const queue = self.pendingSends.get(threadId) ?? [];
        self.pendingSends.delete(threadId);
        return [...queue];
      },

      /**
       * WHA-3330: optimistic-send entry point. Called by the chat input
       * when the user hits Send. Adds the user message to the thread
       * immediately so they see it; then either dispatches the SSE
       * request right away (no pending attachments) or stashes the
       * params in `queuedSend` for a deferred dispatch (one or more
       * chips still extracting). The state-sync hook flushes the queue
       * the moment every chip leaves PRE_EXTRACTION_STATES.
       */
      submitMessage: flow(function* submitMessage(params: {
        threadId: string;
        message: string;
        userId: string;
        attachedSourceFilenames?: string[];
        /**
         * The part of `message` the user typed, without the attachment headers
         * `useChatSend` prepends. The length limit counts this, not `message`.
         */
        userText?: string;
        analytics?: AssistantSendAnalytics;
        /**
         * The chips this message was sent with, already taken out of the
         * composer by the caller (`takeComposerAttachments`). Omitted, the
         * composer's current chips are used and cleared here.
         */
        attachedDocuments?: Array<{ id: string; state: string | null }>;
      }): Generator<
        any,
        | {
            queued: false;
            result: {
              message: string;
              generatedTitle: string | null;
              assistantMessageId: string | null;
              sourceFilenames: string[];
            };
          }
        | { queued: true; result: null },
        any
      > {
        const { attachedDocuments: carried, ...sendParams } = params;
        const trimmedMessage = sendParams.message.trim();
        // Refuse BEFORE the optimistic add: see `assertCanSend`.
        assertCanSend(sendParams);
        const session = currentAuthSession();
        if (!session) throw new ChatSendRejectedError("Authentication required");

        // Optimistic add — user sees their message right away, regardless
        // of whether we dispatch now or defer. Mirrors the existing
        // sendMessage behaviour; the SSE flow will skip its own add via
        // `skipOptimisticAdd: true`.
        const optimisticMessage = addUserMessage(sendParams.threadId, trimmedMessage);

        // Snapshot the current chips + their states. We need this BEFORE
        // clearing the input area; after clearing, we still want to know
        // which ids are mid-extraction so DocChipGroup inside the
        // user-message bubble can keep spinning for them.
        const attachedDocs =
          carried ??
          self.attachedDocuments.map((d) => ({
            id: d.id,
            state: d.state ?? null,
          }));
        const attachedDocIds = attachedDocs.map((d) => d.id);
        const pendingDocIds = attachedDocs
          .filter((d) => PRE_EXTRACTION_STATES.has(d.state ?? ""))
          .map((d) => d.id);

        // Seed the per-id loading set so chips inside the user-message
        // bubble immediately render as spinning. The state-sync hook
        // keeps it in sync from realtime UPDATEs and clears the flag
        // when state leaves the pre-extraction set.
        for (const id of pendingDocIds) {
          self.loadingDocIds.add(id);
        }

        // WHA-3330: always clear the input chip area on submit — the
        // chip visually moves into the user message bubble (rendered
        // by DocChipGroup parsing the [ATTACHED DOCUMENTS] header in
        // message.content). Chips with loadingDocIds[id]=true keep
        // spinning inside the bubble until extraction lands.
        if (!carried) {
          for (const id of attachedDocIds) {
            self.removeAttachedDocument(id);
          }
          self.clearAttachedIssues();
        }

        if (pendingDocIds.length > 0) {
          const queued = {
            params: { ...sendParams, message: trimmedMessage },
            attachedDocIds,
            sendBy: Date.now() + QUEUED_SEND_MAX_WAIT_MS,
            optimisticMessage,
            ...session,
          };
          self.queuedSend = queued;
          if (queuedSendTimer) clearTimeout(queuedSendTimer);
          queuedSendTimer = setTimeout(() => {
            queuedSendTimer = null;
            if (self.queuedSend !== queued) return;
            void getRoot<ChatRootAccess>(self).chatStore.flushQueuedSend();
          }, QUEUED_SEND_MAX_WAIT_MS);
          return { queued: true, result: null };
        }

        // No pending attachments — dispatch immediately.
        const result = yield (self as any).sendMessage({
          ...sendParams,
          message: trimmedMessage,
          skipOptimisticAdd: true,
        });
        return { queued: false, result };
      }),

      /**
       * WHA-3330: drain the queued submit. Called by the state-sync hook
       * (useChatbotAttachmentStateSync) the moment every attached doc
       * has left PRE_EXTRACTION_STATES. Clears the chips that were part
       * of the queued message (leaving any newer chips the user
       * attached while waiting), then dispatches the SSE request that
       * was stashed at submit time.
       */
      flushQueuedSend: flow(function* flushQueuedSend() {
        const queued = self.queuedSend;
        if (!queued) return;
        // Clear immediately so a transient error during dispatch can't
        // re-fire this from the state-sync reaction.
        self.queuedSend = null;
        if (queuedSendTimer) clearTimeout(queuedSendTimer);
        queuedSendTimer = null;
        const session = currentAuthSession();
        if (
          !session ||
          session.authUserId !== queued.authUserId ||
          session.authEpoch !== queued.authEpoch
        ) {
          for (const id of queued.attachedDocIds) self.loadingDocIds.delete(id);
          const messages = self.threads.get(queued.params.threadId)?.messages;
          const index = messages?.indexOf(queued.optimisticMessage) ?? -1;
          if (index >= 0) messages?.splice(index, 1);
          return;
        }
        // A send forced at `sendBy` goes out while a document is still
        // processing. Its chip keeps spinning while the state-sync hook
        // watches it finish, for a bounded time: a parse that never ends
        // must not keep a channel and a poll open for the rest of the session.
        const stalled = queued.attachedDocIds.filter((id) => self.loadingDocIds.has(id));
        if (stalled.length > 0) {
          const timer = setTimeout(() => {
            stalledTrackingTimers.delete(timer);
            for (const id of stalled)
              getRoot<ChatRootAccess>(self).chatStore.setDocLoading(id, false);
          }, STALLED_ATTACHMENT_TRACKING_MS);
          stalledTrackingTimers.add(timer);
        }
        try {
          yield (self as any).sendMessage({
            ...queued.params,
            skipOptimisticAdd: true,
          });
        } catch (err) {
          // sendMessage already pushes an error assistant message into
          // the thread + sets self.error; swallow here so the MobX
          // reaction that called us doesn't bubble.
          console.error("flushQueuedSend failed:", err);
        }
      }),

      /** Forget a held send without dispatching it. */
      dropQueuedSend() {
        if (queuedSendTimer) clearTimeout(queuedSendTimer);
        queuedSendTimer = null;
        self.queuedSend = null;
      },

      clearSessionQueues() {
        // Invalidate stream ownership before abort callbacks/finalizers run.
        const controllers = [...streamAbortControllers.values()];
        streamAbortControllers.clear();
        for (const controller of controllers) controller.abort();
        self.authAbortController.abort();
        self.authAbortController = new AbortController();
        prewarmRequests.clear();
        if (queuedSendTimer) clearTimeout(queuedSendTimer);
        queuedSendTimer = null;
        self.queuedSend = null;
        for (const timer of stalledTrackingTimers) clearTimeout(timer);
        stalledTrackingTimers.clear();
        self.pendingSends.clear();
        self.loadingDocIds.clear();
        self.attachedDocuments.clear();
        self.attachedIssues.clear();
        self.sessions.clear();
        self.activeSessions.clear();
        self.threads.clear();
        self.threadLoadingState.clear();
        self.sessionPrewarmStates.clear();
        self.sessionWebSearch.clear();
        self.pendingWebSearch.clear();
        self.sessionProject.clear();
        self.pendingProject.clear();
        self.pendingToolConfirmations.clear();
        self.toolConfirmationSubmissions.clear();
        self.toolConfirmationDenyOnFailure.clear();
        self.pendingAskUser = null;
        self.sessionId = null;
        self.sessionLoading = false;
        self.sessionsLoading = false;
        self.isLoading = false;
        self.statusMessage = null;
        self.error = null;
        for (const key of [
          "sessionWebSearch",
          "pendingWebSearch",
          "sessionProject",
          "pendingProject",
        ]) {
          try {
            localStorage.removeItem(`chat:${key}`);
          } catch {
            logger.warnLocal("Chat preferences could not be cleared from unavailable storage");
          }
        }
      },

      beforeDestroy() {
        if (queuedSendTimer) clearTimeout(queuedSendTimer);
        queuedSendTimer = null;
        for (const timer of stalledTrackingTimers) clearTimeout(timer);
        stalledTrackingTimers.clear();
      },

      /**
       * Delete the last user+assistant exchange from a session (WHA-2637 edit-message).
       * Also removes the last two messages from the local thread.
       */
      deleteLastExchange: flow(function* deleteLastExchange(params: {
        sessionId: string;
        threadId: string;
      }) {
        self.error = null;

        try {
          yield chatRepo.deleteLastExchange(params.sessionId);

          // Remove the last assistant + user message pair from local state
          const thread = self.threads.get(params.threadId);
          if (thread && thread.messages.length >= 2) {
            const last = thread.messages[thread.messages.length - 1];
            const secondLast = thread.messages[thread.messages.length - 2];
            if (last.role === "assistant" && secondLast.role === "user") {
              thread.messages.splice(thread.messages.length - 2, 2);
            }
          }
        } catch (e: unknown) {
          const errorMsg = e instanceof Error ? e.message : String(e);
          self.error = errorMsg;
          throw e;
        }
      }),

      /**
       * Submit feedback for a specific chat message
       */
      submitFeedback: flow(function* submitFeedback(params: {
        messageId: string;
        rating: chatRepo.FeedbackRating;
        comment?: string;
      }) {
        self.error = null;

        try {
          const result: chatRepo.FeedbackResult = yield chatRepo.submitFeedback(params);
          return result;
        } catch (e: unknown) {
          const errorMsg = e instanceof Error ? e.message : String(e);
          self.error = errorMsg;
          throw e;
        }
      }),

      /**
       * Delete feedback for a specific chat message
       */
      deleteFeedback: flow(function* deleteFeedback(messageId: string) {
        self.error = null;

        try {
          yield chatRepo.deleteFeedback(messageId);
        } catch (e: unknown) {
          const errorMsg = e instanceof Error ? e.message : String(e);
          self.error = errorMsg;
          throw e;
        }
      }),

      /**
       * Clear all messages in a thread
       */
      clearThread(threadId: string) {
        const thread = self.threads.get(threadId);
        if (!thread) return;

        thread.messages.clear();
      },

      /**
       * Clear error state
       */
      clearError() {
        self.error = null;
      },

      /**
       * WHA-2873: Submit answer to a pending AskUserQuestion prompt.
       * Called mid-stream — unblocks the backend so Claude can continue.
       */
      submitChoiceAnswer: flow(function* submitChoiceAnswer(selectedLabel: string) {
        const pending = self.pendingAskUser;
        if (!pending) return;

        const questionText = pending.questions[0]?.question ?? "";
        const answers: Record<string, string> = { [questionText]: selectedLabel };

        self.setPendingAskUser(null);

        try {
          yield chatRepo.submitAnswer(pending.questionId, answers);
        } catch (err) {
          console.error("Failed to submit choice answer", err);
        }
      }),

      /**
       * WHA-3058: Submit approve/deny for a pending tool-confirmation.
       * Called mid-stream — unblocks the backend so Claude can continue.
       */
      submitToolConfirmation: flow(function* submitToolConfirmation(
        threadId: string,
        /** The question the CARD THE USER CLICKED was rendered with. */
        questionId: string,
        confirmed: boolean,
        remember = false,
        // WHA-3354: optional tool-arg overrides the user adjusted inside the
        // confirmation card (e.g. picked a different target folder for
        // `add_file_to_folder`). Forwarded to /chatbot/query/answer.
        overrides?: Record<string, unknown>
      ) {
        const { signal } = self.authAbortController;
        const pending = self.pendingToolConfirmations.get(threadId)?.[0];
        // Answer the question the user actually read, never "whatever is at the
        // head now". The head can move between render and click — the server
        // releases a sibling, an expiry retires a card — and these gate real
        // writes (`create_pm_issue`, `add_file_to_folder`, `update_building_data`),
        // so a mismatched approve would apply their decision, and the tool-arg
        // overrides they edited, to a different write than the one on screen.
        // The card is keyed by question id and has already remounted, so the
        // user is looking at the new one and their next click carries ITS id.
        // Dropping the stale click is right; dropping it silently is not —
        // without this line a decision that never reached the backend leaves
        // no trace anywhere.
        if (!pending || pending.questionId !== questionId) {
          console.warn("Chat tool confirmation: dropped a click for a question that moved", {
            threadId,
            clicked: questionId,
            head: pending?.questionId ?? null,
          });
          return;
        }

        // WHA-3318: only persist auto-approve when the user approves.
        // The tool_name on the payload is what the backend keys against.
        const toolName = pending.payload?.tool_name;
        const rememberToolName = confirmed && remember && toolName ? toolName : undefined;

        // Is this click a retry of an answer that already failed? Read before
        // the "submitting" state overwrites it — it is how a route that keeps
        // failing gets unpinned without reading a status code that may never
        // say "pod gone" (see the catch below).
        const previous = self.toolConfirmationSubmissions.get(threadId);
        const isRetryOfFailure =
          previous?.questionId === pending.questionId && previous.state === "failed";
        // Narrower: the previous failure was itself a 5xx, so a 5xx now is the
        // second CONSECUTIVE one. Any other failure in between (offline, a
        // retryable 404) clears the flag when it settles, which is the reset.
        const isRetryOfServerError = isRetryOfFailure && previous?.serverError === true;

        // The card STAYS up until the POST lands. Clearing it first and
        // swallowing the error left the user with a spinner, no buttons, and a
        // question id nobody could answer while the backend still waited.
        self.setToolConfirmationSubmission(threadId, {
          questionId: pending.questionId,
          action: confirmed ? "approve" : "deny",
          state: "submitting",
        });

        let released: string[] = [];
        try {
          const result = yield chatRepo.submitConfirmation(pending.questionId, confirmed, {
            remember: Boolean(rememberToolName),
            rememberToolName,
            overrides,
            route: pending.server,
            signal,
          });
          if (signal.aborted) return;
          // "Always allow" is released SERVER-side (chatbot#181): the siblings
          // are already blocked inside `wait_for_confirmation` having checked
          // the preference before it existed, so the answer endpoint frees them
          // and tells us which. Answering them from here as well would put two
          // answers on one question_id and race a concurrent teardown's deny.
          released = (result as chatRepo.SubmitConfirmationResult | undefined)?.released ?? [];
        } catch (err) {
          if (signal.aborted) return;
          // A teardown ran while this was in flight and left the deny to us.
          const owedDeny = self.toolConfirmationDenyOnFailure.delete(pending.questionId);

          // A card raised without a `server` (POD_IP unset, or a backend older
          // than chatbot#181) is answered routelessly, and the repository
          // cannot read the handler's "no longer pending" as an expiry there:
          // routeless, the replicas that never held the question say exactly
          // the same thing while it is still live on another. So the first one
          // stays retryable — a round-robin retry may land on the right pod —
          // and a retry told the same thing settles as expired, rather than
          // leaving the user with a Try again that can only 404 forever.
          const expired =
            err instanceof chatRepo.ConfirmationExpiredError ||
            (err instanceof chatRepo.ConfirmationSubmitError && err.gone && isRetryOfFailure);

          if (expired) {
            // The backend has stopped waiting on this one, so there is nothing
            // to deny and a retry can never succeed. The card STAYS, showing
            // why — retiring it here would make it vanish on click with no
            // explanation, and its "expired" state would be filtered out as
            // belonging to a question no longer at the head. The user
            // dismisses it, which is what clears both.
            if (!owedDeny) {
              self.settleToolConfirmationSubmission(threadId, pending.questionId, {
                questionId: pending.questionId,
                action: confirmed ? "approve" : "deny",
                state: "failed",
                errorKey: "expired",
              });
            }
            return;
          }

          console.error("Failed to submit tool confirmation", err);
          // Has the pin stopped resolving? Unpinning is ONE-WAY — the answer
          // then round-robins across replicas and can never again reach the
          // process whose registry holds the waiting coroutine — so it takes
          // more than a bare status. Only a 404 the chatbot app did not send
          // (`fromHandler` false: no JSON `detail`, so the ingress or an edge
          // answered) says `?route=` resolves nowhere. A 404 carrying a detail
          // — "Session not found", the framework's "Not Found" — came FROM a
          // pod, which proves the route still reaches one; the repository
          // keeps those retryable and so must this.
          //
          // 404 is not the only shape a dead route takes, either: the sticky
          // proxy `proxy_pass`es to the route with no `proxy_intercept_errors`,
          // so a route naming a deleted pod surfaces as a 502/504 — and keeping
          // the pin through those is what made "Try again" a button that could
          // never work. A 5xx therefore unpins on the SECOND consecutive
          // 5xx, while the first keeps the route: one 502/504 is equally
          // what a briefly overloaded proxy in front of a perfectly healthy pod
          // returns, and that pod is the only one holding the waiter.
          //
          // A transport failure never unpins, however many times it repeats,
          // and it does not count towards those two either. It throws a bare
          // TypeError with no status because the request did not reach the
          // ingress at all — an offline laptop says nothing about the pod, so
          // an offline attempt followed by one 502 has to keep the pin.
          const serverError = err instanceof chatRepo.ConfirmationSubmitError && err.status >= 500;
          const routeIsDead =
            err instanceof chatRepo.ConfirmationSubmitError &&
            ((err.status === 404 && !err.fromHandler) || (serverError && isRetryOfServerError));
          if (owedDeny) {
            // The card is already off screen (Stop, or the stream ended), so
            // there is nobody left to retry. Answer it the way the teardown
            // would have, or the coroutine holds the session lock until the
            // HITL timeout. This is the only shot it gets, so it does not
            // re-send a route that just demonstrated it resolves nowhere.
            denyToolConfirmation(pending.questionId, routeIsDead ? null : pending.server, signal);
            return;
          }
          if (routeIsDead) {
            self.clearToolConfirmationRoute(threadId, pending.questionId);
          }
          self.settleToolConfirmationSubmission(threadId, pending.questionId, {
            questionId: pending.questionId,
            action: confirmed ? "approve" : "deny",
            state: "failed",
            errorKey: "submitFailed",
            serverError,
          });
          return;
        }

        self.toolConfirmationDenyOnFailure.delete(pending.questionId);
        // `released` are already answered server-side; their cards would only
        // ever 404. The SSE `tool_confirmation_resolved` event says the same
        // thing, and `removeToolConfirmations` is idempotent, so whichever
        // arrives first is enough.
        self.removeToolConfirmations(threadId, [pending.questionId, ...released]);
        self.settleToolConfirmationSubmission(threadId, pending.questionId, null);
      }),

      /** Set collapsed state (WHA-2252) */
      setCollapsed(val: boolean) {
        // If collapsing while expanded, restore saved size (not position) and clear
        // expanded state (WHA-2390). Position is NOT restored here because
        // handleToggleCollapse already computed the correct collapsed position;
        // restoring preExpandPosition would overwrite it (WHA-2925).
        if (val && self.isExpanded) {
          if (self.preExpandSize) self.widgetSize = self.preExpandSize;
          self.isExpanded = false;
          self.preExpandSize = null;
          self.preExpandPosition = null;
        }
        self.isCollapsed = val;
      },

      /** Set widget position (WHA-2252) */
      setWidgetPosition(pos: { x: number; y: number }) {
        self.widgetPosition = pos;
      },

      /** Set widget size (WHA-2252) */
      setWidgetSize(size: { width: number; height: number }) {
        self.widgetSize = size;
      },

      /** WHA-3109: Toggle the user-moved flag. Setting true disables sidebar-relative auto-positioning. */
      setUserMovedWidget(moved: boolean) {
        self.hasUserMovedWidget = moved;
      },

      /**
       * Expand to a large centered size — saves current size/position first (WHA-2390).
       * `navSidebarWidth` is the live width of the nav sidebar so the centering math
       * tracks the actual sidebar (collapsed 64 / expanded 224), not a hardcoded value.
       */
      setExpanded(navSidebarWidth = 64) {
        if (self.isExpanded) return;
        self.preExpandSize = { ...self.widgetSize };
        self.preExpandPosition = { ...self.widgetPosition };
        self.expandedNavWidth = navSidebarWidth;

        const layout = expandedChatLayout(navSidebarWidth);
        self.widgetSize = layout.size;
        self.widgetPosition = layout.position;
        self.isExpanded = true;
      },

      /**
       * Recompute the maximized geometry for the current viewport — window
       * resize while expanded must use the same nav-relative centering as
       * setExpanded, not the collapsed pill's bottom-right anchor.
       */
      refreshExpandedLayout() {
        if (!self.isExpanded) return;
        const layout = expandedChatLayout(self.expandedNavWidth);
        self.widgetSize = layout.size;
        self.widgetPosition = layout.position;
      },

      /** Restore from expanded mode to normal sidebar (WHA-2390) */
      setNormalSize() {
        if (!self.isExpanded) return;
        if (self.preExpandSize) {
          self.widgetSize = self.preExpandSize;
        }
        if (self.preExpandPosition) {
          self.widgetPosition = self.preExpandPosition;
        }
        self.isExpanded = false;
        self.preExpandSize = null;
        self.preExpandPosition = null;
      },

      /**
       * Backward-compat adapter: maps old ViewMode strings to new isCollapsed state.
       * When expanding from collapsed, repositions the widget so the panel stays
       * within the visible viewport (WHA-2376).
       */
      setViewMode(mode: ViewMode) {
        if (mode !== "collapsed" && self.isCollapsed) {
          const collapsedBottom = self.widgetPosition.y + COLLAPSED_HEIGHT;
          const expandedHeight = self.widgetSize.height;
          const availableHeight = window.innerHeight - TOP_OFFSET;
          let newY = collapsedBottom - expandedHeight;
          newY = Math.min(newY, availableHeight - expandedHeight - BOTTOM_MARGIN);
          newY = Math.max(0, newY);
          self.widgetPosition = {
            x: Math.max(0, self.widgetPosition.x + COLLAPSED_WIDTH - self.widgetSize.width),
            y: newY,
          };
        }
        self.isCollapsed = mode === "collapsed";
      },

      addAttachedDocument(doc: { id: string; filename: string; state?: string | null }) {
        if (!self.attachedDocuments.find((d) => d.id === doc.id)) {
          self.attachedDocuments.push(
            AttachedDocument.create({
              id: doc.id,
              filename: doc.filename,
              state: doc.state ?? null,
            })
          );
        }
      },
      clearAttachedDocuments() {
        self.attachedDocuments.clear();
      },
      // WHA-3356: update extraction state on an attached doc as the pipeline
      // progresses. No-op if the doc isn't currently attached.
      setAttachedDocumentState(docId: string, state: string | null) {
        const doc = self.attachedDocuments.find((d) => d.id === docId);
        if (doc) {
          doc.state = state;
        }
      },

      // WHA-3330: track whether a doc id is currently in a pre-extraction
      // state. Used by user-message DocChipGroup to render a spinner on the
      // chip while the file is still being extracted in the background.
      setDocLoading(docId: string, loading: boolean) {
        if (loading) {
          self.loadingDocIds.add(docId);
        } else {
          self.loadingDocIds.delete(docId);
        }
      },

      addAttachedIssue(issue: { id: number; issue_number: number; title: string }) {
        if (!self.attachedIssues.find((i) => i.id === issue.id)) {
          self.attachedIssues.push(AttachedIssue.create(issue));
        }
      },
      removeAttachedIssue(issueId: number) {
        const idx = self.attachedIssues.findIndex((i) => i.id === issueId);
        if (idx >= 0) self.attachedIssues.splice(idx, 1);
      },

      // ====================================================================
      // Multi-session actions (WHA-1465)
      // ====================================================================

      /**
       * Load all sessions for a building from the backend
       */
      loadSessions: flow(function* loadSessions(params: { orgId: string; buildingId: string }) {
        const { signal } = self.authAbortController;
        self.sessionsLoading = true;
        self.error = null;

        try {
          const sessions: chatRepo.ChatSessionSummary[] = yield chatRepo.fetchSessions(
            params.orgId,
            Number(params.buildingId),
            signal
          );
          signal.throwIfAborted();

          // Clear existing sessions for this building and add new ones
          const existingIds = Array.from(self.sessions.values())
            .filter((s) => s.buildingId === params.buildingId)
            .map((s) => s.id);
          existingIds.forEach((id) => self.sessions.delete(id));

          sessions.forEach((s) => {
            self.sessions.set(
              s.id,
              ChatSession.create({
                id: s.id,
                title: s.title,
                threadId: s.threadId,
                buildingId: params.buildingId,
                createdAt: s.createdAt,
                lastMessageAt: s.lastMessageAt,
                messageCount: s.messageCount,
              })
            );
          });

          // Set the most recent session as active if none is active
          if (!self.activeSessions.has(params.buildingId) && sessions.length > 0) {
            self.activeSessions.set(params.buildingId, sessions[0].id);
          }

          return sessions;
        } catch (e: unknown) {
          if (signal.aborted) throw e;
          const errorMsg = e instanceof Error ? e.message : String(e);
          self.error = errorMsg;
          throw e;
        } finally {
          if (!signal.aborted) self.sessionsLoading = false;
        }
      }),

      /**
       * Create a new chat session for a building
       */
      createSession: flow(function* createSession(params: { orgId: string; buildingId: string }) {
        const { signal } = self.authAbortController;
        self.error = null;

        try {
          const newSession: chatRepo.CreateSessionResponse = yield chatRepo.createSession(
            params.orgId,
            Number(params.buildingId),
            signal
          );
          signal.throwIfAborted();

          // Add to sessions map
          self.sessions.set(
            newSession.id,
            ChatSession.create({
              id: newSession.id,
              title: newSession.title,
              threadId: newSession.threadId,
              buildingId: params.buildingId,
              createdAt: newSession.createdAt,
              lastMessageAt: null,
              messageCount: 0,
            })
          );

          // Set as active session
          self.activeSessions.set(params.buildingId, newSession.id);

          // Carry over any web search preference the user set before this first
          // session existed (the toggle is now usable on the very first chat).
          self.consumePendingWebSearch(params.buildingId, newSession.id);

          // WHA-3829: same carry-over for a pre-session project scope.
          self.consumePendingProject(params.buildingId, newSession.id);

          // Create an empty hydrated thread immediately so the UI has a clean
          // slate and doesn't flash stale content from the previous session.
          if (!self.threads.has(newSession.threadId)) {
            self.threads.set(
              newSession.threadId,
              ChatThread.create({
                threadId: newSession.threadId,
                buildingId: params.buildingId,
                orgId: params.orgId,
                sessionId: newSession.id,
                messages: [],
                isHydrated: true,
              })
            );
          }

          return newSession;
        } catch (e: unknown) {
          if (signal.aborted) throw e;
          const errorMsg = e instanceof Error ? e.message : String(e);
          self.error = errorMsg;
          throw e;
        }
      }),

      /**
       * Switch to a different chat session
       */
      switchSession(params: { buildingId: string; sessionId: string }) {
        const session = self.sessions.get(params.sessionId);
        if (!session) {
          throw new Error("Session not found");
        }
        self.activeSessions.set(params.buildingId, params.sessionId);
      },

      /**
       * Prewarm a historical session so Claude can resume before the next user query.
       */
      prewarmSession: flow(function* prewarmSession(params: {
        sessionId: string;
        enableWebSearch?: boolean;
        projectId?: string;
      }) {
        const { signal } = self.authAbortController;
        const enableWebSearch = params.enableWebSearch ?? false;
        const prewarmKey = buildSessionPrewarmKey(
          params.sessionId,
          enableWebSearch,
          params.projectId
        );
        const existingRequest = prewarmRequests.get(prewarmKey);
        if (existingRequest) {
          return yield existingRequest;
        }

        self.sessionPrewarmStates.set(prewarmKey, "prewarming");
        const requestPromise = withAbort(
          chatRepo.prewarmSession(params.sessionId, {
            enableWebSearch,
            ...(params.projectId != null && { projectId: Number(params.projectId) }),
            signal,
          }),
          signal
        );
        prewarmRequests.set(prewarmKey, requestPromise);

        try {
          const result: chatRepo.PrewarmSessionResponse = yield requestPromise;
          signal.throwIfAborted();
          const nextState: SessionPrewarmState =
            result.historyReady || result.status === "skipped"
              ? "ready"
              : result.status === "context_only"
                ? "context_only"
                : "failed";
          self.sessionPrewarmStates.set(prewarmKey, nextState);
          return result;
        } catch (e: unknown) {
          if (!signal.aborted) self.sessionPrewarmStates.set(prewarmKey, "failed");
          throw e;
        } finally {
          if (prewarmRequests.get(prewarmKey) === requestPromise)
            prewarmRequests.delete(prewarmKey);
        }
      }),

      waitForSessionPrewarm(params: {
        sessionId: string;
        enableWebSearch?: boolean;
        projectId?: string;
      }) {
        const enableWebSearch = params.enableWebSearch ?? false;
        return (
          prewarmRequests.get(
            buildSessionPrewarmKey(params.sessionId, enableWebSearch, params.projectId)
          ) ?? Promise.resolve(null)
        );
      },

      /**
       * Archive a chat session
       */
      archiveSession: flow(function* archiveSession(params: {
        sessionId: string;
        buildingId: string;
        orgId: string;
        userId: string;
      }) {
        self.error = null;

        try {
          yield chatRepo.archiveSession(params.sessionId);

          // Remove from sessions map
          self.sessions.delete(params.sessionId);

          // If this was the active session, switch to the next available
          if (self.activeSessions.get(params.buildingId) === params.sessionId) {
            const remainingSessions = Array.from(self.sessions.values())
              .filter((s) => s.buildingId === params.buildingId)
              .sort((a, b) => {
                const aTime = a.lastMessageAt || a.createdAt;
                const bTime = b.lastMessageAt || b.createdAt;
                return bTime.localeCompare(aTime);
              });

            if (remainingSessions.length > 0) {
              const newSession = remainingSessions[0];
              self.activeSessions.set(params.buildingId, newSession.id);

              // Load messages for the newly active session if not already hydrated (WHA-2335)
              const thread = self.threads.get(newSession.threadId);
              if (!thread?.isHydrated) {
                yield self.loadSessionMessages({
                  sessionId: newSession.id,
                  threadId: newSession.threadId,
                  buildingId: params.buildingId,
                  orgId: params.orgId,
                  userId: params.userId,
                });
              }
            } else {
              self.activeSessions.delete(params.buildingId);

              // Clear stale thread data so the UI shows a clean welcome state (WHA-2337)
              self.threads.forEach((thread) => {
                if (thread.buildingId === params.buildingId) {
                  thread.messages.clear();
                  thread.isHydrated = false;
                }
              });
            }
          }
        } catch (e: unknown) {
          const errorMsg = e instanceof Error ? e.message : String(e);
          self.error = errorMsg;
          throw e;
        }
      }),

      /**
       * Rename a chat session
       */
      renameSession: flow(function* renameSession(params: { sessionId: string; title: string }) {
        self.error = null;

        try {
          const updated: chatRepo.UpdateSessionResponse = yield chatRepo.renameSession(
            params.sessionId,
            params.title
          );

          const session = self.sessions.get(updated.id);
          if (session) {
            session.title = updated.title;
          }

          return updated;
        } catch (e: unknown) {
          const errorMsg = e instanceof Error ? e.message : String(e);
          self.error = errorMsg;
          throw e;
        }
      }),

      /**
       * Roll back a session to its last successful assistant turn and reload messages.
       */
      rollbackSession: flow(function* rollbackSession(params: {
        sessionId: string;
        threadId: string;
        buildingId: string;
        orgId: string;
      }) {
        const { signal } = self.authAbortController;
        self.error = null;
        self.isLoading = true;
        self.setThreadLoadingState(params.threadId, true, "Refreshing chat...");

        // Cancel in-flight stream for this thread before rollback.
        const existingController = streamAbortControllers.get(params.threadId);
        if (existingController) {
          // Same as a superseding send: the aborted stream's confirmations can
          // never be answered, and its own teardown will skip them.
          self.denyOutstandingToolConfirmations(params.threadId);
          existingController.abort();
          streamAbortControllers.delete(params.threadId);
        }

        // Optimistically drop a trailing failed turn so stale local input is hidden immediately.
        const existingThread = self.threads.get(params.threadId);
        if (existingThread && existingThread.messages.length > 0) {
          const lastMessage = existingThread.messages[existingThread.messages.length - 1];
          if (lastMessage.role === "assistant" && lastMessage.isError) {
            existingThread.messages.splice(existingThread.messages.length - 1, 1);
          }

          const trailingMessage = existingThread.messages[existingThread.messages.length - 1];
          if (trailingMessage?.role === "user") {
            existingThread.messages.splice(existingThread.messages.length - 1, 1);
          }
        }

        try {
          const rollbackResult: chatRepo.RollbackSessionResponse = yield chatRepo.rollbackSession(
            params.sessionId,
            signal
          );
          signal.throwIfAborted();

          const result: chatRepo.SessionMessagesResponse = yield chatRepo.fetchSessionMessages(
            params.sessionId,
            50,
            0,
            signal
          );
          signal.throwIfAborted();
          const normalizedMessages = normalizeRollbackMessages(
            result.messages,
            rollbackResult.rolledBackToMessageId
          );
          const trimmedCount = Math.max(0, result.messages.length - normalizedMessages.length);
          const normalizedResult: chatRepo.SessionMessagesResponse = {
            ...result,
            messages: normalizedMessages,
            total: Math.max(0, result.total - trimmedCount),
          };

          let thread = self.threads.get(params.threadId);
          if (!thread) {
            thread = ChatThread.create({
              threadId: params.threadId,
              buildingId: params.buildingId,
              orgId: params.orgId,
              sessionId: params.sessionId,
              messages: [],
              isHydrated: false,
            });
            self.threads.set(params.threadId, thread);
          }

          thread.messages.replace(
            normalizedResult.messages.map((m) =>
              ChatMessage.create({
                id: m.id,
                role: m.role as "user" | "assistant",
                content: m.content,
                sourceFilenames: m.sourceFilenames ?? [],
                choices: m.choices ?? [],
                choiceQuestion: m.choiceQuestion ?? "",
                usedDocumentTool: m.usedDocumentTool ?? false,
                ehrLink: m.ehrLink ?? null,
              })
            )
          );
          thread.isHydrated = true;

          // When rollback returns zero messages (first-message failure), ensure
          // the thread and store are in a clean state so the UI shows the
          // welcome screen and the input is ready for a fresh message.
          if (normalizedResult.messages.length === 0) {
            self.error = null;
            self.statusMessage = null;
          }

          const session = self.sessions.get(params.sessionId);
          if (session) {
            session.lastMessageAt = rollbackResult.lastMessageAt;
            session.messageCount = normalizedResult.total;
          }

          return {
            rollback: rollbackResult,
            messages: normalizedResult,
          };
        } catch (e: unknown) {
          if (signal.aborted) throw e;
          const errorMsg = e instanceof Error ? e.message : String(e);
          self.error = errorMsg;
          throw e;
        } finally {
          if (!signal.aborted) {
            self.setThreadLoadingState(params.threadId, false);
            self.isLoading = false;
            self.statusMessage = null;
          }
        }
      }),

      /**
       * Update session metadata after sending a message
       */
      updateSessionAfterMessage(sessionId: string) {
        const session = self.sessions.get(sessionId);
        if (session) {
          // Update last_message_at and message count
          session.lastMessageAt = new Date().toISOString();
          session.messageCount = session.messageCount + 1;
        }
      },

      /**
       * Set a session title directly (used for auto-generated titles)
       */
      setSessionTitle(sessionId: string, title: string) {
        const session = self.sessions.get(sessionId);
        if (session) {
          session.title = title;
        }
      },
    };
  })
  .actions((self) => ({
    afterCreate() {
      // Model selection moved to the chatbot backend (chatbot#134) — clear the
      // stale WHA-2545 per-session choice persisted by older builds.
      try {
        localStorage.removeItem("chat:sessionModelChoices");
      } catch {
        // localStorage may be unavailable
      }
      // WHA-2716: Restore per-session web search toggle from localStorage
      try {
        const stored = localStorage.getItem("chat:sessionWebSearch");
        if (stored !== null) {
          const parsed = JSON.parse(stored) as Record<string, boolean>;
          for (const [k, v] of Object.entries(parsed)) {
            if (typeof v === "boolean") {
              self.sessionWebSearch.set(k, v);
            }
          }
        }
      } catch {
        // localStorage may be unavailable
      }
      // Restore pending pre-session web search toggle (set before a building's
      // first chat) from localStorage, so it survives a reload before sending.
      try {
        const stored = localStorage.getItem("chat:pendingWebSearch");
        if (stored !== null) {
          const parsed = JSON.parse(stored) as Record<string, boolean>;
          for (const [k, v] of Object.entries(parsed)) {
            if (typeof v === "boolean") {
              self.pendingWebSearch.set(k, v);
            }
          }
        }
      } catch {
        // localStorage may be unavailable
      }
      // WHA-3829: Restore per-session + pending pre-session project scopes.
      const isProjectScope = (v: unknown): v is ChatProjectScope =>
        typeof v === "object" &&
        v !== null &&
        typeof (v as ChatProjectScope).id === "string" &&
        typeof (v as ChatProjectScope).name === "string";
      for (const [storageKey, map] of [
        ["chat:sessionProject", self.sessionProject],
        ["chat:pendingProject", self.pendingProject],
      ] as const) {
        try {
          const stored = localStorage.getItem(storageKey);
          if (stored !== null) {
            const parsed = JSON.parse(stored) as Record<string, unknown>;
            for (const [k, v] of Object.entries(parsed)) {
              if (isProjectScope(v)) {
                map.set(k, { id: v.id, name: v.name });
              }
            }
          }
        } catch {
          // localStorage may be unavailable
        }
      }
    },
  }));

export type ChatStoreInstance = Instance<typeof ChatStore>;
export type ChatStoreSnapshotIn = SnapshotIn<typeof ChatStore>;
export type ChatStoreSnapshotOut = SnapshotOut<typeof ChatStore>;
export type ChatSessionInstance = Instance<typeof ChatSession>;
